// App shell — sidebar + top bar + view router.
// Bulk arrays (deals/companies/people) are NOT loaded on boot. Each view
// fetches its own data on mount. The App only loads small lookup tables
// (stages, team members, counts, agent catalog) so cold boot stays cheap.

const { useState, useEffect, useMemo, useRef } = React;
const ErrorBoundary = window.ErrorBoundary;

// ⌘K "New company" — NewCompanyDrawer needs the industries lookup, which the
// App shell doesn't load on boot (it's view-owned data). This thin wrapper
// fetches it on open so the palette can create from any screen.
const QuickCompanyDrawer = ({ members, onClose, onCreated }) => {
  const [industries, setIndustries] = useState([]);
  useEffect(() => {
    window.AtlasApi.listIndustries()
      .then((i) => setIndustries(Array.isArray(i) ? i : []))
      .catch(() => setIndustries([]));
  }, []);
  return (
    <window.NewCompanyDrawer industries={industries} members={members || []}
      onClose={onClose} onCreated={onCreated} />
  );
};

// What a create starts from on the page you're on (DealCloud's "create from
// here"). `ctx` is what the deal / company / contact page published
// (window.publishPageContext); the result feeds the quick-create drawers and
// the "+ New" menu's hints. A buyer engagement always belongs to a SELLER
// deal: on a seller deal it is that deal, on a buyer's pursuit the deal it
// pursues (with the buyer picked), on a buyer firm's page the firm.
const _CONTACT_TYPES = ['seller', 'buyer', 'gpp', 'professional', 'internal'];

// The page each view belongs to — a detail view counts as its list page.
// Drives the role page guard below and which record links can navigate.
const PAGE_FOR_VIEW = {
  deal: 'deals', company: 'companies', contact: 'people', buyer: 'buyers', user: 'people', cim: 'deals', industry: 'industries',
  workspace: 'agents', 'lost-deals': 'agents', views: 'analytics', view: 'analytics',
  integrations: 'settings', dashboard: 'home',
  // A shared Ask Atlas thread rides the ⌘K page: no search, no Ask Atlas.
  ask: 'search',
};
const createPrefillFor = (ctx) => {
  const ref = (id, name) => (id ? { id, name: name || '' } : null);
  const empty = { engagement: {}, buyer: {}, contact: {}, task: null, hints: {} };
  if (!ctx) return empty;
  if (ctx.kind === 'deal') {
    const pursuit = ctx.type === 'buyer_buyside';
    const company = ref(ctx.company && ctx.company.id, ctx.company && ctx.company.name);
    const deal = pursuit ? (ctx.sellerDeal || null) : ref(ctx.id, ctx.name);
    return {
      engagement: { company: pursuit ? null : company },
      buyer: { deal, buyer: pursuit ? company : null },
      contact: { company, type: pursuit ? 'buyer' : undefined },
      task: { engagement: ref(ctx.id, ctx.name), company },
      hints: {
        engagement: !pursuit && company ? `for ${company.name}` : null,
        buyer_engagement: deal ? `on ${deal.name}` : null,
        contact: company ? `at ${company.name}` : null,
        task: `on ${ctx.name}`,
      },
    };
  }
  if (ctx.kind === 'company') {
    const company = ref(ctx.id, ctx.name);
    return {
      engagement: { company },
      buyer: ctx.isBuyer ? { buyer: company } : { deal: ctx.sellerDeal || null },
      contact: { company, type: ctx.isBuyer ? 'buyer' : undefined },
      task: { company },
      hints: {
        engagement: `for ${ctx.name}`,
        buyer_engagement: ctx.isBuyer ? `${ctx.name} as the buyer` : (ctx.sellerDeal ? `on ${ctx.sellerDeal.name}` : null),
        contact: `at ${ctx.name}`,
        task: `on ${ctx.name}`,
      },
    };
  }
  if (ctx.kind === 'contact') {
    const company = ref(ctx.company && ctx.company.id, ctx.company && ctx.company.name);
    const buyerSide = ctx.contactType === 'buyer';
    return {
      engagement: { company: buyerSide ? null : company },
      buyer: buyerSide && company ? { buyer: company } : {},
      contact: { company, type: _CONTACT_TYPES.includes(ctx.contactType) ? ctx.contactType : undefined },
      task: { contact: ref(ctx.id, ctx.name), company },
      hints: {
        engagement: !buyerSide && company ? `for ${company.name}` : null,
        buyer_engagement: buyerSide && company ? `${company.name} as the buyer` : null,
        contact: company ? `at ${company.name}` : null,
        task: `on ${ctx.name}`,
      },
    };
  }
  return empty;
};

// "+ New" next to search — always there, and it reads the page: each item
// says what it will start from ("on Diversified IS", "at Gemspring").
const CreateMenu = ({ onCreate, hints = {}, canSee = () => true }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const items = [
    { kind: 'engagement',       label: 'Engagement',       icon: <Icon.Pipeline size={13} />, page: 'deals' },
    { kind: 'buyer_engagement', label: 'Buyer engagement', icon: <Icon.Pipeline size={13} />, page: 'deals' },
    { kind: 'company',          label: 'Company',          icon: <Icon.Building size={13} />, page: 'companies' },
    { kind: 'contact',          label: 'Contact',          icon: <Icon.User size={13} />,     page: 'people' },
    { kind: 'task',             label: 'Task',             icon: <Icon.Check size={13} />,    page: 'inbox' },
  ].filter((i) => canSee(i.page));
  if (!items.length) return null;
  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen((v) => !v)} title="Create — starts from the page you're on" aria-haspopup="menu" aria-expanded={open}
        style={{
          height: 30, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 10px',
          border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer', fontSize: 12.5, fontWeight: 500,
          background: open ? 'var(--surface-2)' : 'var(--surface)', color: 'var(--text-2)', fontFamily: 'inherit',
        }}>
        <Icon.Plus size={12} /> New
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40, minWidth: 250, padding: 4,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }}>
          {items.map((i) => (
            <button key={i.kind} role="menuitem" onClick={() => { setOpen(false); onCreate(i.kind); }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 8px', border: 'none',
                borderRadius: 5, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
              }}>
              <span style={{ color: 'var(--text-3)', display: 'inline-flex' }}>{i.icon}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text)', whiteSpace: 'nowrap' }}>{i.label}</span>
              {hints[i.kind] && (
                <span style={{ marginLeft: 'auto', paddingLeft: 10, fontSize: 11, color: 'var(--text-4)', maxWidth: 170,
                               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={hints[i.kind]}>
                  {hints[i.kind]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const App = () => {
  // Derive the initial view + selected entity from the URL hash. Lets users
  // bookmark or share a link like #/deals/abc-123 and land directly on that
  // deal's detail panel instead of the home page.
  //   • parseHash() returns null for unknown / non-router hashes (including
  //     leftover Supabase auth callbacks if AuthGate somehow didn't strip
  //     them) — we fall through to {view:'home'} in that case.
  //   • Initial state runs once via the useState initializer. After mount,
  //     a hashchange listener (below) keeps state synced when the browser
  //     fires back/forward.
  const initialRoute = (window.AtlasRouter && window.AtlasRouter.parseHash()) || { view: 'home' };
  const [view, setView] = useState(initialRoute.view || 'home');
  const [dealId,    setDealId]    = useState(initialRoute.dealId    || null);
  const [companyId, setCompanyId] = useState(initialRoute.companyId || null);
  const [contactId, setContactId] = useState(initialRoute.contactId || null);
  const [workspaceId, setWorkspaceId] = useState(initialRoute.workspaceId || null);
  const [userId, setUserId] = useState(initialRoute.userId || null);  // team-member profile
  const [viewId, setViewId] = useState(initialRoute.viewId || null);  // opened saved view
  const [buyerId, setBuyerId] = useState(initialRoute.buyerId || null);  // buyer page (#/buyers/<id>)
  // The CIM Command Center (#/cim/<engagement id>) — one seller deal's CIM
  // build. Its tab is a ?tab= refinement the page reads and writes itself.
  const [cimId, setCimId] = useState(initialRoute.cimId || null);
  // An industry page (#/industries/<slug>).
  const [industrySlug, setIndustrySlug] = useState(initialRoute.industrySlug || null);
  // An Ask Atlas thread opened by its share link (#/ask/<id>).
  const [askId, setAskId] = useState(initialRoute.askId || null);
  // The note open in the Notes view's modal. Lifted out of NotesView so it
  // rides the URL (#/notes/<id>) — that's the link the note modal's Share
  // button copies, and what makes a shared note open straight to itself.
  const [noteId, setNoteId] = useState(initialRoute.noteId || null);
  // A record ID from the URL (#/id/<n>) waiting to be looked up — see below.
  const [recordNo, setRecordNo] = useState(initialRoute.recordNo || null);
  // Which Settings pane to open on next mount. Reset on every nav (see
  // switchView) so the sidebar/top-bar Settings entry lands on the default
  // pane; deep links (e.g. Agents → "New agent") override it via goToSettings.
  const [settingsPane, setSettingsPane] = useState(null);
  // Optional stage slug to pre-filter the Engagements list on next mount,
  // set by the dashboard's "Pipeline by stage" rows. Reset on every nav.
  const [dealsStageFilter, setDealsStageFilter] = useState(null);
  const [dealsDealmakerFilter, setDealsDealmakerFilter] = useState(null);  // {id, name} — deep-link from analytics
  // Where a Home metric card lands: {tab, sort} — the tab whose count it shows,
  // plus an optional initial sort for cards whose number is a sum rather than a
  // count (Pipeline est. fees opens fees-descending).
  const [dealsTabFilter, setDealsTabFilter] = useState(null);
  const [viewsPrefill, setViewsPrefill] = useState(null);  // {source, filters, columns, title} — drill drawer "Open as table"
  const [askAtlas, setAskAtlas] = useState(null);          // {query} — Ask Atlas panel, opened from ⌘K
  const [cmdOpen, setCmdOpen] = useState(false);
  // ⌘K / "+ New" quick-create — which drawer is open
  // ('engagement'|'buyer_engagement'|'company'|'contact'|'task').
  const [quickCreate, setQuickCreate] = useState(null);
  // The record the open page is about, published by the deal / company /
  // contact pages (window.publishPageContext) so a create starts from it —
  // DealCloud's "create from here": on a seller deal, New buyer engagement
  // has the deal picked; on a company, New contact has the company.
  const [pageCtx, setPageCtx] = useState(null);
  React.useEffect(() => {
    window.publishPageContext = (ctx, ownerId) => setPageCtx((cur) => {
      if (ctx) return ctx;
      // A page clearing on unmount only clears its own context.
      return cur && ownerId && cur.id !== ownerId ? cur : null;
    });
    return () => { window.publishPageContext = null; };
  }, []);
  // Phone / iPad / desktop (src/device.jsx). A phone gets PhoneShell (bottom
  // tabs, full-screen pages); everything else keeps the sidebar layout below.
  const device = window.useDevice();
  // Collapsible sidebar — persisted, so a narrow window can reclaim ~220px.
  const [sidebarOpen, setSidebarOpen] = useState(() => { try { return localStorage.getItem('atlas.sidebarOpen') !== '0'; } catch (_) { return true; } });
  // iPad portrait / Split View (device.compact): the sidebar floats over the
  // page instead of taking a third of the width — closed by default, closed
  // again on every navigation, and it never touches the persisted desktop flag.
  const [overlayNavOpen, setOverlayNavOpen] = useState(false);
  const toggleSidebar = () => {
    if (device.compact) { setOverlayNavOpen((v) => !v); return; }
    setSidebarOpen((v) => { const next = !v; try { localStorage.setItem('atlas.sidebarOpen', next ? '1' : '0'); } catch (_) {} return next; });
  };
  const [tweaks, setTweak] = useTweaks(window.__TWEAK_DEFAULTS);
  const [theme, setThemeState] = useState(() => document.documentElement.dataset.theme || 'light');
  const setTheme = (next) => {
    document.documentElement.dataset.theme = next;
    try { localStorage.setItem('atlas.theme', next); } catch (_) {}
    setThemeState(next);
  };
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  // Small lookup data only — bulk arrays are owned by individual views.
  const [agentCatalog, setAgentCatalog] = useState(null);
  const [counts, setCounts]       = useState(null);
  const [stages, setStages]       = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeUserId, setActiveUserIdState] = useState(() => localStorage.getItem('atlas.activeUserId'));
  // Role the logged-in JWT user holds (admin / editor / viewer). Populated
  // by /api/auth/whoami on boot. Determines whether the TopBar exposes
  // the "Switch user" UI — only admins get it.
  const [currentRole, setCurrentRole] = useState(null);
  // Blast-manager capability from /auth/whoami — gates the Blast nav entry.
  const [canManageBlasts, setCanManageBlasts] = useState(false);
  const [canViewSignals, setCanViewSignals] = useState(false);
  const [canUseDialer, setCanUseDialer] = useState(false);
  // whoami.can_edit_records (permissions.edit_records + member override); null = not told.
  const [canEditRecords, setCanEditRecords] = useState(null);
  // whoami.team_member_id — who actually signed in, never who an admin browses
  // as. Ask Atlas share links are the signed-in owner's call alone.
  const [signedInMemberId, setSignedInMemberId] = useState(null);
  // Pages this member's role sees (whoami.visible_pages); null = every page.
  // Settings → Permissions → "What each role sees" edits it per role.
  // A deal guest (whoami.deal_guest — backend app/deal_guest.py) gets "Your
  // deals" and the deal pages under it, read-only. Remembered across loads
  // so a returning guest never flashes the full shell before whoami answers;
  // the server holds the line either way.
  const guestHint = (() => { try { return localStorage.getItem('atlas.dealGuest') === '1'; } catch (_) { return false; } })();
  const [isGuest, setIsGuest] = useState(guestHint);
  window.AtlasIsGuest = isGuest;
  const [visiblePages, setVisiblePages] = useState(guestHint ? ['deals'] : null);
  const canSee = React.useCallback((key) => !visiblePages || visiblePages.includes(key), [visiblePages]);
  window.AtlasCanSee = canSee;
  const [mode, setMode]           = useState(window.AtlasData?.__mode || 'mock');
  // Phone only: the boot loaders have answered, so a 'mock' mode now means the
  // API is unreachable (it starts as 'mock' until the first loader succeeds).
  const [phoneBootDone, setPhoneBootDone] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [forceMock, setForceMockState] = useState(() => window.AtlasMockApi?.forceMock || false);

  // Toggling the data-source pill: persist the choice in localStorage (so it
  // survives reloads), then reload the page so every view re-fetches from the
  // newly-selected source. A soft re-fetch would work too but a full reload is
  // unambiguous — no stale array state lurking in any view.
  // Set the data source to a specific value (true=mock, false=live). The
  // dropdown menu calls this; same value as current is a no-op (menu close only).
  const setMockMode = (wantsMock) => {
    if (wantsMock === forceMock) return;
    window.AtlasMockApi?.setForceMock(wantsMock);
    setForceMockState(wantsMock);
    window.location.reload();
  };

  const setActiveUserId = (id) => {
    if (id) localStorage.setItem('atlas.activeUserId', id);
    else localStorage.removeItem('atlas.activeUserId');
    // UI prefs are keyed to the effective user, so impersonating someone else
    // must drop the cached layout and refetch theirs rather than showing ours.
    try { window.clearUiPrefsCache?.(); } catch (_) {}
    setActiveUserIdState(id);
  };

  // Subscribe to live/mock mode flips from any loader
  useEffect(() => window.AtlasData.onModeChange(setMode), []);

  // Load small/lookup data on mount. Per-view data (deals/companies/people)
  // is fetched by the views themselves.
  useEffect(() => {
    let alive = true;
    // Phone: the counts aggregate is slow (3–6 s on cellular) and only the
    // Deals chips read it — don't hold whoami / team members (and with them
    // activeUser, which every phone page keys off) hostage to it.
    const countsApart = !!(window.AtlasDevice && window.AtlasDevice.phone);
    if (countsApart) window.AtlasData.loadDashboardCounts().then((c) => { if (alive) setCounts(c); }).catch(() => {});
    // The team's record layout (whoami.record_layout) lands as soon as whoami
    // answers, not after the slow counts below: a deep-linked record re-keys
    // before anyone works in it. apiFetch dedups, so it's the same request.
    // An admin keeps a ⌘K preview over it; a change dispatches 'atlas-layout'.
    window.AtlasApi.whoami().then((me) => {
      if (alive && me) window.setRecordTeamLayout(me.record_layout, { admin: (me.role || '').toLowerCase() === 'admin' });
    }).catch(() => {});
    (async () => {
      const [cat, ct, tm, st, me] = await Promise.all([
        window.AtlasData.loadAgentCatalog(),
        countsApart ? Promise.resolve(undefined) : window.AtlasData.loadDashboardCounts(),
        window.AtlasData.loadTeamMembers(),
        window.AtlasData.loadStages(),
        // /api/auth/whoami — tells us our role + matched team_member id.
        // Treated as best-effort: if it fails, we fall through to the
        // legacy localStorage path so the UI stays usable for legacy
        // bearer-token deploys.
        window.AtlasApi.whoami().catch(() => null),
      ]);
      if (!alive) return;
      if (countsApart) setPhoneBootDone(true);
      // Warm the integration-catalog cache so note pills can render provider
      // logos (Fireflies / Outlook) anywhere in the app — not just after the
      // user has opened the Integrations settings page. Fire-and-forget.
      if (window.AtlasApi.integrationCatalog) {
        window.AtlasApi.integrationCatalog()
          .then((c) => { window.__integrationCatalogCache = c || { providers: [] }; })
          .catch(() => {});
      }
      setAgentCatalog(cat);
      if (!countsApart) setCounts(ct);
      setStages(st);
      const liveTeam = Array.isArray(tm) ? tm : [];
      setTeamMembers(liveTeam);

      // Bind the SPA's identity to the JWT, not localStorage. This pins
      // non-admin users to themselves: even if their localStorage has a
      // different activeUserId, we overwrite it. Admins still get the
      // switcher in TopBar.
      // Role drives admin-only UI (e.g. the Analytics section) and is
      // independent of whether whoami returned a matched team_member_id —
      // the mock/demo whoami returns a role with no id, so set it up front.
      if (me) setCurrentRole(me.role || null);
      if (me) setCanManageBlasts(!!me.can_manage_blasts);
      if (me) setCanViewSignals(!!me.can_view_signals);
      if (me) setCanUseDialer(!!me.can_use_dialer);
      if (me) setCanEditRecords(me.can_edit_records ?? null);
      if (me) setSignedInMemberId(me.team_member_id || null);
      if (me) setVisiblePages(Array.isArray(me.visible_pages) ? me.visible_pages : null);
      if (me) {
        setIsGuest(!!me.deal_guest);
        try {
          if (me.deal_guest) localStorage.setItem('atlas.dealGuest', '1');
          else localStorage.removeItem('atlas.dealGuest');
        } catch (_) {}
      }
      // First-run scope defaults by role (no-ops once the user has ever
      // touched the toggle): admins land on All teams.
      if (me?.role) window.AtlasApi?.seedAllTeamsDefault?.(me.role);
      if (me?.team_member_id) {
        // Force activeUserId to match the logged-in person unless they
        // already have one set (admin who's impersonating).
        const stale = localStorage.getItem('atlas.activeUserId');
        const isAdmin = (me.role || '').toLowerCase() === 'admin';
        if (!stale || !isAdmin) {
          setActiveUserId(me.team_member_id);
        }
      } else if (!activeUserId && liveTeam.length) {
        // Legacy fallback (no whoami response — bearer-token deploys).
        const fallback = liveTeam.find((u) => u.role === 'admin') || liveTeam[0];
        setActiveUserId(fallback.id);
      }
    })();
    return () => { alive = false; };
  }, [reloadTick]);

  // The counts endpoint is scoped per active user (my_team buckets), so a
  // user switch needs a re-pull — the boot effect above only runs on mount.
  // Guard against the initial render: boot already fetches counts once.
  const countsUserRef = React.useRef(null);
  useEffect(() => {
    if (countsUserRef.current === null) { countsUserRef.current = activeUserId; return; }
    if (countsUserRef.current === activeUserId) return;
    countsUserRef.current = activeUserId;
    window.AtlasData.loadDashboardCounts().then(setCounts).catch(() => {});
  }, [activeUserId]);

  // Re-pull counts whenever an inline edit fires the global `deal-updated`
  // event, so the sidebar sub-buckets stay accurate.
  useEffect(() => {
    const refresh = () => window.AtlasData.loadDashboardCounts().then(setCounts).catch(() => {});
    window.addEventListener('deal-updated', refresh);
    window.addEventListener('atlas-data-changed', refresh);
    return () => {
      window.removeEventListener('deal-updated', refresh);
      window.removeEventListener('atlas-data-changed', refresh);
    };
  }, []);

  // Record pages open Ask Atlas directly ("Ask AI" on the deal header) —
  // the panel is hosted here, so they ask via a window event. The page
  // context (deal/company/contact) rides in automatically from `view`.
  useEffect(() => {
    // detail.conversationId opens that thread instead (a shared link's
    // "Continue in my own chat").
    const onAsk = (e) => {
      if (window.AtlasIsGuest) return;
      const d = e.detail || {};
      setAskAtlas({ query: d.query || '', conversationId: d.conversationId || null });
    };
    window.addEventListener('ask-atlas', onAsk);
    return () => window.removeEventListener('ask-atlas', onAsk);
  }, []);

  // Cmd-K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (!(window.AtlasCanSee ? window.AtlasCanSee('search') : true)) return;
        setCmdOpen((v) => !v);
      }
      if (e.key === 'Escape') setCmdOpen(false);
    };
    // The sidebar's "Search…" button (and the phone top bar) ask via event.
    const onOpenCmd = () => { if (window.AtlasCanSee ? window.AtlasCanSee('search') : true) setCmdOpen(true); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-cmd', onOpenCmd);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('open-cmd', onOpenCmd);
    };
  }, []);

  // Record layout (record_panels.jsx window.recordV2: the team's, from whoami,
  // or an admin's ⌘K preview). Every change dispatches 'atlas-layout'; the record
  // pages are keyed `${id}:${recordKey}` so they remount instead of changing their
  // hook set (React #310). A phone renders its own pages whatever the layout, so
  // its key stays 'phone' and a whoami answer never remounts it.
  const [recordLayout, setRecordLayout] = useState(() => (window.recordV2() ? 'v2' : 'classic'));
  useEffect(() => {
    const onLayout = (e) => setRecordLayout(e && e.detail && e.detail.layout === 'v2' ? 'v2' : 'classic');
    window.addEventListener('atlas-layout', onLayout);
    return () => window.removeEventListener('atlas-layout', onLayout);
  }, []);
  const recordKey = device.phone ? 'phone' : recordLayout;

  // The floating iPad sidebar closes once you've picked where to go.
  useEffect(() => { setOverlayNavOpen(false); }, [view, dealId, companyId, contactId]);

  useEffect(() => {
    document.documentElement.style.setProperty('--row-h',
      tweaks.density === 'compact' ? '32px' :
      tweaks.density === 'spacious' ? '40px' : '36px');
  }, [tweaks.density]);

  useEffect(() => {
    // Each accent has light/dark variants. --accent stays roughly the same
    // (it's an interactive color, needs contrast on either background), but
    // --accent-soft (used as a tint) and --accent-text flip dramatically.
    const map = {
      atlas:  {
        light: { accent: 'oklch(0.55 0.14 255)', soft: 'oklch(0.96 0.025 255)', text: 'oklch(0.42 0.14 255)' },
        dark:  { accent: 'oklch(0.68 0.14 255)', soft: 'oklch(0.28 0.05 255)',  text: 'oklch(0.82 0.14 255)' },
      },
      indigo: {
        light: { accent: 'oklch(0.50 0.18 285)', soft: 'oklch(0.96 0.03 285)',  text: 'oklch(0.40 0.18 285)' },
        dark:  { accent: 'oklch(0.66 0.18 285)', soft: 'oklch(0.28 0.06 285)',  text: 'oklch(0.80 0.18 285)' },
      },
      forest: {
        light: { accent: 'oklch(0.45 0.10 155)', soft: 'oklch(0.95 0.04 155)',  text: 'oklch(0.36 0.10 155)' },
        dark:  { accent: 'oklch(0.62 0.12 155)', soft: 'oklch(0.26 0.05 155)',  text: 'oklch(0.78 0.13 155)' },
      },
      ember:  {
        light: { accent: 'oklch(0.55 0.16 35)',  soft: 'oklch(0.96 0.04 35)',   text: 'oklch(0.45 0.15 35)' },
        dark:  { accent: 'oklch(0.68 0.16 35)',  soft: 'oklch(0.28 0.07 35)',   text: 'oklch(0.82 0.15 35)' },
      },
    };
    const family = map[tweaks.accent] || map.atlas;
    const c = family[theme === 'dark' ? 'dark' : 'light'];
    document.documentElement.style.setProperty('--accent', c.accent);
    document.documentElement.style.setProperty('--accent-soft', c.soft);
    document.documentElement.style.setProperty('--accent-text', c.text);
  }, [tweaks.accent, theme]);

  const activeUser = teamMembers.find((u) => u.id === activeUserId) || null;
  // What the record pages' write gate reads (record_panels.jsx recordCanWrite).
  // NOT window.AtlasAuth — that is the session module (views/login.jsx).
  window.AtlasWhoami = { known: currentRole != null, role: currentRole, canEditRecords, hasActiveUser: !!activeUser, memberId: signedInMemberId };

  // Ctrl/⌘+click on any entity link opens the detail in a new browser tab
  // (hash routes deep-link, so the new tab lands on the record directly).
  // Handlers almost never forward their click event, so we read the live
  // `window.event` the browser sets while a handler runs — checking
  // type === 'click' so programmatic navigations (e.g. from a keydown)
  // never get hijacked by a held modifier.
  const isNewTabClick = () => {
    // The native shell has no tabs — window.open would hand a bare hash URL
    // to Safari. An iPad hardware keyboard's ⌘ must not trigger it.
    if (window.__ATLAS_NATIVE__) return false;
    const ev = window.event;
    return !!(ev && ev.type === 'click' && (ev.metaKey || ev.ctrlKey));
  };
  const openedInNewTab = (state, params) => {
    if (!isNewTabClick()) return false;
    const hash = window.AtlasRouter?.buildHash?.(state);
    if (!hash) return false;
    const q = params ? window.RecordPanelsCore.buildQuery(params) : '';
    window.open('#' + hash + (q ? '?' + q : ''), '_blank', 'noopener');
    return true;
  };
  // A role that can't open a page never navigates into it: a record link to
  // it (a note's company chip, a buyer row, a dealmaker's name) does nothing,
  // instead of mounting a page whose APIs refuse it and bouncing back.
  const canOpen = (v) => canSee(PAGE_FOR_VIEW[v] || v);
  // A record link may carry the page's own query (?panel=…&note=…, views/record_panels.jsx).
  // Same record: one planned history write; another record: a hash link the router follows.
  const RECORD_NAV = {
    deal:    { state: (id) => ({ view: 'deal', dealId: id }),       open: (id) => setDealId(id),    current: () => dealId },
    company: { state: (id) => ({ view: 'company', companyId: id }), open: (id) => setCompanyId(id), current: () => companyId },
    contact: { state: (id) => ({ view: 'contact', contactId: id }), open: (id) => setContactId(id), current: () => contactId },
  };
  const goToRecord = (kind, id, params) => {
    if (!canOpen(kind)) return;
    const core = window.RecordPanelsCore;
    const nav = RECORD_NAV[kind];
    const p = core.linkParams(params);                     // drops a stray click event (calendar.jsx passes one)
    if (openedInNewTab(nav.state(id), p)) return;
    if (!p) { nav.open(id); setView(kind); return; }
    const cur = nav.current();
    if (view === kind && core.sameRecord({ kind, id: cur }, { kind, id })) {
      const patch = p.panel ? Object.assign(core.openPatch(p.panel, p), p.note ? { note: p.note } : null) : p;
      window.setRecordParams({ kind, id: cur }, patch, { via: 'link' });
      return;
    }
    window.location.hash = core.buildRecordHash(kind, id, p);
  };
  const goToDeal    = (id, params) => goToRecord('deal', id, params);
  const goToCompany = (id, params) => goToRecord('company', id, params);
  const goToContact = (id, params) => goToRecord('contact', id, params);
  const goToWorkspace = (id) => { if (!canOpen('workspace')) return; if (openedInNewTab({ view: 'workspace', workspaceId: id })) return; setWorkspaceId(id); setView('workspace'); };
  const goToUser = (id) => { if (!canOpen('user')) return; if (openedInNewTab({ view: 'user', userId: id })) return; setUserId(id); setView('user'); };
  const goToView = (id) => { if (!canOpen('view')) return; if (openedInNewTab({ view: 'view', viewId: id })) return; setViewId(id); setView('view'); };
  // A buyer is its company's page (202609232300). Plain click: resolve the
  // roster row to its company and go straight there, no intermediate history
  // entry. Cmd-click / middle-click: open #/buyers/<id>, which BuyerRedirect
  // resolves in the new tab.
  const goToBuyer = (id) => {
    if (!canOpen('buyer')) return;
    if (openedInNewTab({ view: 'buyer', buyerId: id })) return;
    window.AtlasApi.getBuyer({ buyer_id: id })
      .then((b) => { if (b && b.company_id) goToCompany(b.company_id); else { setBuyerId(id); setView('buyer'); } })
      .catch(() => { setBuyerId(id); setView('buyer'); });
  };
  // The deal team's CIM build for one seller deal. `tab` lands on one of the
  // page's tabs (overview|room|vault|intelligence|sections): it rides the
  // hash as ?tab=, which writeHash keeps as the page's own refinement. Never
  // for a deal guest — the page is the deal team's, not the deal's.
  const goToCim = (id, tab) => {
    if (!id || isGuest || !canOpen('cim')) return;
    const hash = window.AtlasRouter?.buildHash?.({ view: 'cim', cimId: id });
    const full = hash && tab ? hash + '?tab=' + encodeURIComponent(tab) : hash;
    if (isNewTabClick() && full) { window.open('#' + full, '_blank', 'noopener'); return; }
    // Already on this CIM page: just switch its tab (it follows hashchange).
    if (tab && full && view === 'cim' && cimId === id) {
      try { history.replaceState(null, '', '#' + full); } catch (_) {}
      window.dispatchEvent(new HashChangeEvent('hashchange'));
      return;
    }
    if (full && tab) { try { history.pushState(null, '', '#' + full); } catch (_) {} }
    setCimId(id); setView('cim');
  };
  const reloadAll = () => setReloadTick((t) => t + 1);

  // Global navigation handle — lets self-contained views rendered via
  // window.XYZ (Integrations, Cleanup, etc.) jump to a company/contact/deal
  // detail without needing the full setView/setCompanyId prop chain. The
  // ref is updated each render so it always closes over the latest setters.
  React.useEffect(() => {
    window.AtlasNav = { goToDeal, goToCompany, goToContact, goToWorkspace, goToUser, goToView, goToBuyer, goToCim, goToDealsDealmaker, openViewBuilder, switchView };
  });

  // A role limited to some pages (a caller: Calling only) never lands on the
  // rest — deep links, old bookmarks and Home all fall through to its first
  // page. Detail views count as their list page.
  React.useEffect(() => {
    if (!visiblePages || !visiblePages.length) return;
    const page = PAGE_FOR_VIEW[view] || view;
    if (!visiblePages.includes(page)) switchView(visiblePages[0]);
  }, [visiblePages, view]);   // eslint-disable-line react-hooks/exhaustive-deps

  // A deal guest never gets the CIM Command Center, even off a pasted link —
  // it lands on the deal page instead. Replaces the /cim entry, so Back
  // doesn't bounce through it again.
  React.useEffect(() => {
    if (!isGuest || view !== 'cim') return;
    const next = cimId ? { view: 'deal', dealId: cimId } : { view: 'deals' };
    try { history.replaceState(null, '', '#' + window.AtlasRouter.buildHash(next)); } catch (_) {}
    setCimId(null);
    if (next.dealId) setDealId(next.dealId);
    setView(next.view);
  }, [isGuest, view]);   // eslint-disable-line react-hooks/exhaustive-deps

  // When switching to a non-detail view, clear the detail-id state so we don't
  // strand stale selections.
  const switchView = (v) => {
    // Several call sites pair a redundant `setView('deal')` with the goTo*
    // that carries the record id. When ctrl/⌘+click turns that goTo* into a
    // new-tab open, the bare setView would strand THIS page on a detail view
    // with no id (blank screen) — skip it; the goTo* handles the click.
    if (isNewTabClick() && ['deal', 'company', 'contact', 'user', 'workspace', 'view', 'buyer', 'cim', 'industry'].includes(v)) return;
    if (v !== 'deal')    setDealId(null);
    if (v !== 'company') setCompanyId(null);
    if (v !== 'contact') setContactId(null);
    if (v !== 'user')    setUserId(null);
    if (v !== 'view')    setViewId(null);
    if (v !== 'buyer')   setBuyerId(null);
    if (v !== 'cim')     setCimId(null);
    if (v !== 'industry') setIndustrySlug(null);
    if (v !== 'ask')     setAskId(null);
    if (v !== 'notes')   setNoteId(null);
    setSettingsPane(null);
    setDealsStageFilter(null);
    setDealsDealmakerFilter(null);
    setDealsTabFilter(null);
    setView(v);
  };

  // Navigate to a specific Settings pane. switchView resets settingsPane to
  // null first, so setting it after wins (both setters batch in the handler).
  const goToSettings = (pane = null) => {
    switchView('settings');
    if (pane) setSettingsPane(pane);
  };

  // The top-level Integrations page was retired from the nav (it rendered the
  // Settings → Integrations pane verbatim); old #/integrations links land on
  // that pane instead.
  React.useEffect(() => {
    if (view === 'integrations') goToSettings('integrations');
  }, [view]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Jump to the Engagements list pre-filtered to one pipeline stage (from the
  // dashboard's "Pipeline by stage" rows). switchView clears the filter first,
  // so the value set here wins for this navigation only.
  // `tabKey` comes from the panel's own scope: that aggregate is role-scoped
  // server-side, so for a dealmaker the stage rows describe THEIR deals and
  // have to open the Mine tab — landing on team-wide Active would answer with
  // a different population than the row that was clicked.
  const goToDealsStage = (stageSlug, tabKey = null) => {
    switchView('deals');
    if (stageSlug) setDealsStageFilter(stageSlug);
    if (tabKey) setDealsTabFilter({ tab: tabKey, sort: null });
  };

  // Jump to the Engagements list on a specific tab — the Home metric cards
  // (Pipeline / On hold / Mine / Exited) each open the rows behind the number.
  // `scope` (mine|team) lets the sender pin the ownership scope its number
  // was counted in; omitted → the Engagements view's own mapping decides.
  const goToDealsTab = (tabKey, sort = null, scope = null) => {
    switchView('deals');
    if (tabKey) setDealsTabFilter({ tab: tabKey, sort, scope });
  };

  // Jump to the Engagements list filtered to one dealmaker (deep-link from a
  // clickable dealmaker name in Analytics — team members have no profile page).
  const goToDealsDealmaker = (id, name) => {
    switchView('deals');
    if (id) setDealsDealmakerFilter({ id, name: name || null });
  };

  // Open the view builder (on the Analytics page) pre-loaded with a source +
  // filters — the drill drawer's "Open as table" (a full-page grid of the
  // records behind a chart element). AnalyticsView consumes the prefill and
  // clears it via the callback.
  const openViewBuilder = (prefill) => {
    switchView('analytics');
    setViewsPrefill(prefill || null);
  };

  // ── URL ↔ state sync ────────────────────────────────────────────────────
  // Write the URL whenever the navigation state changes. AtlasRouter.writeHash
  // is a no-op if the resulting hash matches the current one, so this also
  // safely runs on mount with the initial state we just derived.
  React.useEffect(() => {
    if (!window.AtlasRouter) return;
    window.AtlasRouter.writeHash({ view, dealId, companyId, contactId, userId, workspaceId, viewId, noteId, buyerId, cimId, recordNo, industrySlug, askId });
  }, [view, dealId, companyId, contactId, userId, workspaceId, viewId, noteId, buyerId, cimId, recordNo, industrySlug, askId]);

  // Listen for browser back/forward, which fires `hashchange`. We re-parse
  // the URL and push the resulting state in. Setters are stable refs so we
  // only need to bind once.
  //
  // Note: programmatic hash writes via pushState do NOT fire hashchange, so
  // there's no feedback loop with the write-effect above.
  React.useEffect(() => {
    if (!window.AtlasRouter) return;
    const onHashChange = () => {
      const next = window.AtlasRouter.parseHash();
      if (!next) return;  // ignore unknown / auth-callback hashes
      setView(next.view || 'home');
      setDealId(next.dealId       || null);
      setCompanyId(next.companyId || null);
      setContactId(next.contactId || null);
      setUserId(next.userId || null);
      setWorkspaceId(next.workspaceId || null);
      setViewId(next.viewId || null);
      setBuyerId(next.buyerId || null);
      setCimId(next.cimId || null);
      setIndustrySlug(next.industrySlug || null);
      setAskId(next.askId || null);
      setNoteId(next.noteId || null);
      setRecordNo(next.recordNo || null);
      // Deep-link prefills are one-shot, scoped to the click that set them —
      // switchView clears them, and this path has to as well. They aren't in
      // the URL, so leaving them set meant a Back into /deals silently
      // re-applied the last drill-down (a stage, a dealmaker, or a Home metric
      // card's tab + widened filters) to a navigation nobody aimed there.
      setDealsStageFilter(null);
      setDealsDealmakerFilter(null);
      setDealsTabFilter(null);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // A record ID in the URL (#/id/10201815, or #/companies/10201815 — the ID on
  // a record page, the same number DealCloud shows) opens the record it names,
  // whatever its type. Looked up once; the canonical link then REPLACES the
  // numeric one, so Back never bounces through the lookup again.
  const pendingNo = !window.AtlasRouter ? null
    : view === 'record' ? recordNo
    : view === 'company' && window.AtlasRouter.isRecordNumber(companyId) ? companyId
    : view === 'deal' && window.AtlasRouter.isRecordNumber(dealId) ? dealId
    : view === 'contact' && window.AtlasRouter.isRecordNumber(contactId) ? contactId
    : null;
  React.useEffect(() => {
    if (!pendingNo) return;
    let live = true;
    window.AtlasApi.recordByNumber(pendingNo).then((r) => {
      if (!live) return;
      const next = { company: { view: 'company', companyId: r.id }, deal: { view: 'deal', dealId: r.id },
                     contact: { view: 'contact', contactId: r.id } }[r && r.type];
      if (!next) throw new Error('unknown record type');
      const raw = window.location.hash || '';
      const query = raw.includes('?') ? raw.slice(raw.indexOf('?') + 1) : '';   // #/id/<n>?panel=vault keeps its pop-out
      try { history.replaceState(null, '', '#' + window.AtlasRouter.buildHash(next) + (query ? '?' + query : '')); } catch (_) {}
      setRecordNo(null);
      setCompanyId(next.companyId || null);
      setDealId(next.dealId || null);
      setContactId(next.contactId || null);
      setView(next.view);
    }).catch(() => {
      if (!live) return;
      pushToast({ type: 'error', title: `No record with ID ${pendingNo}` });
      setRecordNo(null);
      setCompanyId(null);
      setDealId(null);
      setContactId(null);
      setView('home');
    });
    return () => { live = false; };
  }, [pendingNo]);

  // Only the page actually on screen counts — a context left behind by a
  // page that hasn't cleared yet must not prefill anything.
  const ctxOnScreen = pageCtx && !pendingNo && (
    (pageCtx.kind === 'deal' && view === 'deal' && pageCtx.id === dealId) ||
    (pageCtx.kind === 'company' && view === 'company' && pageCtx.id === companyId) ||
    (pageCtx.kind === 'contact' && view === 'contact' && pageCtx.id === contactId)
  ) ? pageCtx : null;
  const createPrefill = createPrefillFor(ctxOnScreen);

  const sidebarEl = !isGuest && <Sidebar
        view={view}
        setView={switchView}
        setDealId={goToDeal}
        onOpenDealsTab={goToDealsTab}
        counts={counts}
        activeUser={activeUser}
        activeTeamName={activeUser?.team_name || null}
        currentRole={currentRole}
        canManageBlasts={canManageBlasts}
        canViewSignals={canViewSignals}
        canUseDialer={canUseDialer}
        canSee={canSee}
        onCollapse={toggleSidebar}
      />;

  // Which page is on screen: a new one clears the page boundary's fallback
  // (and is a step in the phone shell's back stack).
  const routeKey = [view, dealId, companyId, contactId, userId, workspaceId, viewId, noteId, buyerId, cimId, industrySlug, askId].join('|');

  // A panel that throws closes itself instead of taking the page down.
  const closeOnCrash = (close) => () => {
    close();
    pushToast({ type: 'error', title: 'Something went wrong' });
  };

  // The page for the current route — shared by both shells. On a phone, the
  // Phone* views below take over the list pages (cards instead of grids);
  // record pages adapt in place.
  const pageAllowed = (!visiblePages || visiblePages.includes(PAGE_FOR_VIEW[view] || view)) && !(isGuest && view === 'cim');
  const desktopViews = pageAllowed && <>
          {view === 'home'      && <Home stages={stages} activeUser={activeUser} counts={counts} setView={switchView} setDealId={goToDeal} setWorkspaceId={goToWorkspace} onOpenStage={goToDealsStage} onOpenDealsTab={goToDealsTab} />}
          {view === 'deals'     && isGuest && <window.GuestDealsView setDealId={goToDeal} activeUser={activeUser} />}
          {view === 'deals'     && !isGuest && <DealsView stages={stages} activeUser={activeUser} counts={counts} setView={switchView} setDealId={goToDeal} setCompanyId={goToCompany} initialStageFilter={dealsStageFilter} initialDealmaker={dealsDealmakerFilter} initialTab={dealsTabFilter?.tab} initialSort={dealsTabFilter?.sort} tabDeepLink={dealsTabFilter} />}
          {pendingNo && (
            <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Opening ID {pendingNo}…</div>
          )}
          {/* Record pages are keyed by id and record layout (recordKey; 'phone'
              on a phone): a jump from one record to another remounts the page,
              so per-record state (the outreach tracker, open drawers,
              half-typed edits) never carries over, and a layout flip remounts
              instead of changing hooks. */}
          {view === 'deal' && dealId && !pendingNo && <DealDetail key={`${dealId}:${recordKey}`} dealId={dealId} stages={stages} activeUser={activeUser} back={() => window.AtlasListState.returnTo('deals') || switchView('deals')} />}
          {view === 'cim' && cimId && window.CimCommandCenter && <window.CimCommandCenter key={cimId} engagementId={cimId} phone={!!device.phone} back={() => goToDeal(cimId)} />}
          {view === 'companies' && <CompaniesView setView={switchView} setCompanyId={goToCompany} />}
          {view === 'company' && companyId && !pendingNo && <CompanyDetail key={`${companyId}:${recordKey}`} companyId={companyId} activeUser={activeUser} setView={switchView} setDealId={goToDeal} setContactId={goToContact} back={() => switchView('companies')} />}
          {view === 'people'    && <PeopleView setView={switchView} setContactId={goToContact} />}
          {view === 'referrals' && <window.ReferralsView setView={switchView} setContactId={goToContact} setCompanyId={goToCompany} setDealId={goToDeal} activeUser={activeUser} />}
          {view === 'contact' && contactId && !pendingNo && <ContactDetail key={`${contactId}:${recordKey}`} contactId={contactId} activeUser={activeUser} setView={switchView} setDealId={goToDeal} setCompanyId={goToCompany} back={() => switchView('people')} />}
          {view === 'user' && userId && <UserProfile userId={userId} teamMembers={teamMembers} stages={stages} currentRole={currentRole} selfId={activeUserId} setDealId={goToDeal} setCompanyId={goToCompany} onViewEngagements={goToDealsDealmaker} back={() => switchView('home')} />}
          {view === 'calendar'  && <window.CalendarView activeUser={activeUser} currentRole={currentRole} goToCompany={goToCompany} goToContact={goToContact} />}
          {view === 'notes'     && <NotesView setView={switchView} setDealId={goToDeal} activeUser={activeUser} openNoteId={noteId} setOpenNoteId={setNoteId} />}
          {view === 'files'     && <FilesView setView={switchView} setDealId={goToDeal} />}
          {view === 'cleanup'   && <window.CleanupView setView={switchView} setDealId={goToDeal} setCompanyId={goToCompany} setContactId={goToContact} currentRole={currentRole} />}
          {view === 'marketing' && <window.MarketingView setCompanyId={goToCompany} setContactId={goToContact} currentRole={currentRole} />}
          {view === 'blast'     && <window.BlastView setCompanyId={goToCompany} />}
          {view === 'buyers'    && <BuyersView setCompanyId={goToCompany} setDealId={goToDeal} />}
          {view === 'buyer' && buyerId && <window.BuyerRedirect buyerId={buyerId} setCompanyId={goToCompany} back={() => switchView('buyers')} />}
          {view === 'industries' && window.IndustriesView && <window.IndustriesView />}
          {view === 'industry' && industrySlug && window.IndustryPage && <window.IndustryPage key={industrySlug} slug={industrySlug} />}
          {view === 'ask' && askId && window.SharedAskThread && <window.SharedAskThread key={askId} threadId={askId} activeUser={activeUser} phone={!!device.phone} />}
          {view === 'gpp'       && <GppView setContactId={goToContact} activeUser={activeUser} />}
          {view === 'prospecting' && <window.ProspectingView goToCompany={goToCompany} />}
          {view === 'referral-partners' && <window.ReferralPartnersView setContactId={goToContact} activeUser={activeUser} />}
          {view === 'events'    && <window.LeadEventsView activeUser={activeUser} currentRole={currentRole} isAdmin={(currentRole || '').toLowerCase() === 'admin'} setContactId={goToContact} />}
          {view === 'agents'    && <AgentsView catalog={agentCatalog} goToSettings={goToSettings} openAgentPage={(key) => { if (key === 'lost-deals-finder') switchView('lost-deals'); }} />}
          {view === 'lost-deals' && <window.LostDealsView goToAgents={() => switchView('agents')} goToCompany={goToCompany} />}
          {view === 'signals'    && <window.SignalsView goToCompany={goToCompany} allowed={canViewSignals} />}
          {view === 'calling'    && <window.CallingView allowed={canUseDialer} />}
          {view === 'workspace' && workspaceId && <window.WorkspaceDetail workspaceId={workspaceId} back={() => switchView('home')} />}
          {view === 'email-copilot' && window.EmailCopilotView && <window.EmailCopilotView currentRole={currentRole} />}
          {view === 'activity'  && <window.ActivityView setView={switchView} goToCompany={goToCompany} goToDeal={goToDeal} goToContact={goToContact} />}
          {/* Team activity — one admin page, two time grains. The view key stays
              per tab so #/team-pulse and #/daily-reports keep working. */}
          {view === 'team-pulse' && <window.TeamPulseView switcher={<window.TeamActivitySwitch tab="weekly" onTab={switchView} />} />}
          {view === 'daily-reports' && <window.DealmakerDailyView switcher={<window.TeamActivitySwitch tab="daily" onTab={switchView} />} />}
          {view === 'inbox'     && <InboxView activeUser={activeUser} setView={switchView} setDealId={goToDeal} />}
          {view === 'analytics' && <window.AnalyticsView activeUser={activeUser} currentRole={currentRole} setView={switchView} goToView={goToView} builderPrefill={viewsPrefill} onPrefillConsumed={() => setViewsPrefill(null)} />}
          {/* Legacy route — views now live on the Analytics page. Old #views links land there. */}
          {view === 'views'  && <window.AnalyticsView activeUser={activeUser} currentRole={currentRole} setView={switchView} goToView={goToView} initialMode="dashboards" />}
          {view === 'view' && viewId && <window.ViewsView viewId={viewId} goToView={goToView} back={() => switchView('views')} activeUser={activeUser} />}
          {view === 'settings'  && <SettingsView initialPane={settingsPane} currentRole={currentRole} selfId={activeUserId} />}
          </>;

  const overlays = <>
      {/* resetKey={cmdOpen}: Cmd stays mounted, so closing it is what clears a crash. */}
      <ErrorBoundary name="command" resetKey={cmdOpen} fallback={null} onError={closeOnCrash(() => setCmdOpen(false))}>
        <Cmd
          open={cmdOpen}
          onClose={() => setCmdOpen(false)}
          navigate={(target, payload) => {
            if (target === 'deal'    && payload) goToDeal(payload);
            else if (target === 'company' && payload) goToCompany(payload);
            else if (target === 'contact' && payload) goToContact(payload);
            else switchView(target);
          }}
          onAction={(kind) => setQuickCreate(kind)}
          onAskAtlas={(query) => setAskAtlas({ query })}
          onCommand={(cmd, arg) => {
            if (cmd === 'record_layout') {
              // An admin's own preview; 'team' drops it (the team's layout is Settings → Feature flags).
              const preview = arg === 'team' ? null : arg === 'v2';
              if (window.setRecordV2(preview)) {
                pushToast({ type: 'success', title: preview == null ? 'Record layout: team'
                  : preview ? 'Preview record layout: new' : 'Preview record layout: classic' });
              }
            }
          }}
        />
      </ErrorBoundary>

      {/* Ask Atlas — AI chat off the ⌘K search bar. Page context lets "this
          deal / this company" ground without a search. */}
      {askAtlas && (
        <ErrorBoundary name="ask-atlas" fallback={null} onError={closeOnCrash(() => setAskAtlas(null))}>
          <window.AssistantPanel
            open
            onClose={() => setAskAtlas(null)}
            initialQuery={askAtlas.query}
            initialConversationId={askAtlas.conversationId}
            context={
              pendingNo ? null
              : view === 'deal' && dealId ? { entity_type: 'engagement', id: dealId }
              : view === 'cim' && cimId ? { entity_type: 'engagement', id: cimId }
              : view === 'company' && companyId ? { entity_type: 'company', id: companyId }
              : view === 'contact' && contactId ? { entity_type: 'contact', id: contactId }
              : null
            }
            onOpenView={(id) => { setAskAtlas(null); goToView(id); }}
            activeUser={activeUser}
          />
        </ErrorBoundary>
      )}

      {/* ⌘K quick-create drawers — hosted here so "New …" works from any
          screen. Each lands on the created record to finish filling it in. */}
      <ErrorBoundary name="quick-create" resetKey={quickCreate} fallback={null} onError={closeOnCrash(() => setQuickCreate(null))}>
        {(quickCreate === 'engagement' || quickCreate === 'buyer_engagement') && (
          <NewEngagementDrawer
            stages={stages || []}
            activeUser={activeUser}
            initialType={quickCreate === 'buyer_engagement' ? 'buyer_buyside' : undefined}
            initialCompany={quickCreate === 'buyer_engagement' ? undefined : createPrefill.engagement.company}
            initialDeal={createPrefill.buyer.deal}
            initialBuyer={createPrefill.buyer.buyer}
            onClose={() => setQuickCreate(null)}
            onCreated={(eng) => {
              setQuickCreate(null);
              window.dispatchEvent(new CustomEvent('atlas-data-changed'));
              if (eng?.id) goToDeal(eng.id);
            }}
          />
        )}
        {quickCreate === 'contact' && (
          <NewPersonDrawer
            initialCompany={createPrefill.contact.company}
            initialType={createPrefill.contact.type}
            onClose={() => setQuickCreate(null)}
            onCreated={(contact) => {
              setQuickCreate(null);
              window.dispatchEvent(new CustomEvent('atlas-data-changed'));
              if (contact?.id) goToContact(contact.id);
            }}
          />
        )}
        {quickCreate === 'company' && (
          <QuickCompanyDrawer
            members={teamMembers}
            onClose={() => setQuickCreate(null)}
            onCreated={(company) => {
              setQuickCreate(null);
              window.dispatchEvent(new CustomEvent('atlas-data-changed'));
              if (company?.id) goToCompany(company.id);
            }}
          />
        )}
        {quickCreate === 'task' && (
          // TaskDrawer: was "NewTaskDrawer" here, a name that no longer
          // exists since the Tasks rename — ⌘K "New task" threw.
          <TaskDrawer
            activeUser={activeUser}
            members={teamMembers}
            initialLinks={createPrefill.task}
            onClose={() => setQuickCreate(null)}
            onCreated={() => {
              // If the Inbox is mounted, let it refresh its task list.
              window.dispatchEvent(new CustomEvent('task-created'));
            }}
          />
        )}
      </ErrorBoundary>

      <ErrorBoundary name="hosts" fallback={null}>
        <ToastHost />
        <ConfirmHost />
        <window.OwnerScopeHost />

        <TweaksPanel title="Tweaks">
          <TweakSection label="Theme">
            <TweakRadio
              label="Accent"
              value={tweaks.accent}
              onChange={(v) => setTweak('accent', v)}
              options={['atlas', 'indigo', 'forest', 'ember']}
            />
            <TweakRadio
              label="Density"
              value={tweaks.density}
              onChange={(v) => setTweak('density', v)}
              options={['compact', 'comfortable', 'spacious']}
            />
            <TweakToggle
              label="Show ⌘K hint"
              value={tweaks.showCommandHint}
              onChange={(v) => setTweak('showCommandHint', v)}
            />
          </TweakSection>
        </TweaksPanel>
      </ErrorBoundary>
  </>;

  // ── Phone shell ─────────────────────────────────────────────────────────
  if (device.phone) {
    const isAdmin = (currentRole || '').toLowerCase() === 'admin';
    return (
      <>
        <window.PhoneShell
          view={view}
          activePage={PAGE_FOR_VIEW[view] || view}
          isDetail={['deal', 'company', 'contact', 'user', 'workspace', 'view', 'buyer', 'cim', 'industry', 'ask'].includes(view) || (view === 'notes' && !!noteId)}
          routeKey={routeKey}
          pageTitle={ctxOnScreen ? ctxOnScreen.name : null}
          canSee={canSee}
          isGuest={isGuest}
          isAdmin={isAdmin}
          flags={{ canManageBlasts, canViewSignals, canUseDialer }}
          activeUser={activeUser}
          teamMembers={teamMembers}
          onSwitchUser={isAdmin ? setActiveUserId : null}
          theme={theme}
          onToggleTheme={toggleTheme}
          switchView={switchView}
          goToDeal={goToDeal}
          backTo={() => (view === 'cim' && cimId ? goToDeal(cimId) : switchView(view === 'ask' ? 'home' : PAGE_FOR_VIEW[view] || (view === 'notes' ? 'notes' : 'home')))}
          onSearch={canSee('search') ? () => setCmdOpen(true) : null}
          onAsk={isGuest ? null : () => setAskAtlas({ query: '' })}
          onCreate={isGuest ? null : (kind) => setQuickCreate(kind)}
          createHints={createPrefill.hints}
          onSignOut={signOutOfAtlas}
          offline={phoneBootDone && !forceMock && mode === 'mock'}
        >
          <div key={activeUserId || 'anon'} style={{ height: '100%', overflow: 'hidden' }}>
            <ErrorBoundary name="page" resetKey={routeKey}>
              {pageAllowed && <window.PhoneRoute
                view={view}
                isGuest={isGuest}
                desktopViews={desktopViews}
                props={{
                  stages, counts, activeUser, currentRole, teamMembers, isGuest,
                  setView: switchView, goToDeal, goToCompany, goToContact,
                  dealsTabFilter, dealsStageFilter,
                }}
              />}
            </ErrorBoundary>
          </div>
        </window.PhoneShell>
        {overlays}
      </>
    );
  }

  // ── Desktop / iPad shell ────────────────────────────────────────────────
  // iPad portrait (compact) floats the sidebar over the page; otherwise it's
  // the persisted inline sidebar exactly as on desktop.
  const compactNav = device.compact;
  return (
    <div style={{ display: 'flex', height: '100%', background: 'var(--bg)' }}>
      {!compactNav && sidebarOpen && sidebarEl}
      {compactNav && overlayNavOpen && sidebarEl && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex' }}>
          <div onClick={() => setOverlayNavOpen(false)} style={{ position: 'absolute', inset: 0, background: 'oklch(0.2 0.01 270 / 0.22)' }} />
          <div style={{ position: 'relative', height: '100%', paddingTop: 'var(--sat)', paddingBottom: 'var(--sab)', paddingLeft: 'var(--sal)', background: 'var(--surface-2)', boxShadow: 'var(--shadow-lg)' }}>
            {sidebarEl}
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          sidebarOpen={compactNav ? overlayNavOpen : sidebarOpen}
          onToggleSidebar={isGuest ? null : toggleSidebar}
          onCmdK={() => setCmdOpen(true)}
          onCreate={isGuest ? undefined : (kind) => setQuickCreate(kind)}
          createHints={createPrefill.hints}
          canSee={canSee}
          showHint={tweaks.showCommandHint && !device.touch}
          compact={device.touch}
          mode={mode}
          forceMock={forceMock}
          onSetMock={setMockMode}
          onReload={reloadAll}
          activeUser={activeUser}
          teamMembers={teamMembers}
          onSwitchUser={setActiveUserId}
          onOpenSettings={() => setView('settings')}
          theme={theme}
          onToggleTheme={toggleTheme}
          canSwitchUsers={(currentRole || '').toLowerCase() === 'admin'}
          isAdmin={(currentRole || '').toLowerCase() === 'admin'}
        />

        {/* key={activeUserId}: switching users must remount every view. The
            X-Active-User header re-scopes each request, but most views fetch
            in mount-time effects — without the remount they keep showing the
            previous user's data until a hard refresh. */}
        <div key={activeUserId || 'anon'} style={{ flex: 1, overflow: 'hidden' }}>
          {/* A page outside the role's pages never mounts — not even for the
              one render before the guard above moves off it, when its
              requests would only be refused (a deal guest landing on Home). */}
          <ErrorBoundary name="page" resetKey={routeKey}>
            {desktopViews}
          </ErrorBoundary>
        </div>
      </div>

      {overlays}
    </div>
  );
};

// Stable hash → avatar color so the same user always gets the same dot
const userAvatarColor = (key) => {
  const hues = [
    'oklch(0.50 0.14 30)','oklch(0.50 0.13 200)','oklch(0.50 0.13 295)',
    'oklch(0.50 0.13 145)','oklch(0.50 0.14 355)','oklch(0.50 0.13 90)',
  ];
  let h = 0;
  for (let i = 0; i < (key || '').length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return hues[Math.abs(h) % hues.length];
};

// === Data-source pill — opens a small dropdown menu instead of toggling on
//     click, so the user has to choose deliberately. Two stable states +
//     "offline" rendering when the live API can't be reached.
const DataSourcePill = ({ mode, forceMock, onSetMock, onRetry }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const offline = !forceMock && mode === 'mock';
  const bg   = forceMock ? 'var(--amber-bg)' : offline ? 'var(--rose-bg)'  : 'var(--green-bg)';
  const fg   = forceMock ? 'var(--amber-fg)' : offline ? 'var(--rose-fg)'  : 'var(--green-fg)';
  const dot  = forceMock ? 'oklch(0.65 0.13 75)' : offline ? 'oklch(0.55 0.14 25)' : 'oklch(0.55 0.14 155)';
  const label = forceMock ? 'Mock data' : offline ? 'API offline' : 'Live API';

  const pick = (wantsMock) => {
    setOpen(false);
    if (wantsMock === forceMock) return;  // already in that mode → no-op
    onSetMock(wantsMock);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Data source — click to change"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, height: 24, padding: '0 8px',
          borderRadius: 999, background: bg, color: fg,
          border: '1px solid transparent', cursor: 'pointer', fontSize: 11, fontWeight: 600,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: 999, background: dot }} />
        {label}
        <Icon.ChevronD size={10} />
      </button>

      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 30, right: 0, zIndex: 60, minWidth: 300,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
        }}>
          <div style={{
            padding: '8px 12px', fontSize: 10, fontWeight: 700,
            color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em',
            borderBottom: '1px solid var(--border)', background: 'var(--surface-2)',
          }}>
            Data source
          </div>

          <MenuRadioItem
            selected={!forceMock}
            title="Live API"
            badge={offline ? { hue: 'rose', text: 'offline' } : null}
            sub="Reads and writes go to the real database."
            onClick={() => pick(false)}
          />
          <MenuRadioItem
            selected={forceMock}
            title="Mock data"
            badge={{ hue: 'amber', text: 'demo' }}
            sub="In-browser fixtures only — a handful of demo companies and deals. Edits reset on reload. Safe for screenshots."
            onClick={() => pick(true)}
            last
          />

          {offline && onRetry && (
            <button
              onClick={() => { setOpen(false); onRetry(); }}
              style={{
                width: '100%', padding: '9px 12px', textAlign: 'left',
                background: 'var(--surface-2)', border: 'none', borderTop: '1px solid var(--border)',
                cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)', fontWeight: 500,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <Icon.Refresh size={11} /> Retry the live API
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Radio-style row shared by the top-bar pickers (data source, team scope):
// highlighted dot for the selected option, title + description per row.
const MenuRadioItem = ({ selected, title, sub, badge, onClick, last }) => (
  <button
    onClick={onClick}
    role="menuitemradio"
    aria-checked={selected}
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%',
      padding: '10px 12px', textAlign: 'left',
      background: selected ? 'var(--accent-soft)' : 'var(--surface)',
      border: 'none', borderBottom: last ? 'none' : '1px solid var(--border)',
      cursor: 'pointer',
    }}
    onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'var(--surface-hover)'; }}
    onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'var(--surface)'; }}
  >
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 14, height: 14, borderRadius: 999, marginTop: 2, flexShrink: 0,
      border: '1.5px solid', borderColor: selected ? 'var(--accent)' : 'var(--border-strong)',
      background: '#fff',
    }}>
      {selected && <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />}
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
        {badge && <Pill hue={badge.hue} sz="sm">{badge.text}</Pill>}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3, lineHeight: 1.45 }}>{sub}</div>
    </div>
  </button>
);

// === Top bar ===
// My team vs All teams. Flipping it persists the scope (api.jsx reads it on
// every list call) and reloads so every view re-fetches firm-wide. Mainly
// for non-admins — it lets them find records assigned to another team
// (incl. misassignments) or unowned rows that team-scoping otherwise hides.
// Same state-pill + radio-menu shape as the data-source pill: the button
// names the scope you're IN, the menu names what you can pick. As a bare
// toggle the label read both ways — "does 'My team' mean I'm scoped to it,
// or that clicking scopes me to it?" (Thomas, 08-03).
const ScopeToggle = () => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const all = window.AtlasApi?.isAllTeams?.() || false;
  const pick = (wantsAll) => {
    setOpen(false);
    if (wantsAll === all) return;   // already in that scope → no-op
    window.AtlasApi?.setAllTeams?.(wantsAll);
    window.AtlasListState?.dropParam?.('scope');
    window.location.reload();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Team scope — click to change"
        style={{
          height: 28, padding: '0 10px', borderRadius: 6, cursor: 'pointer',
          border: '1px solid ' + (all ? 'var(--accent)' : 'var(--border)'),
          background: all ? 'var(--accent-soft)' : 'var(--surface)',
          color: all ? 'var(--accent-text)' : 'var(--text-2)',
          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500,
        }}
      >
        <Icon.Users size={13} />
        {all ? 'All teams' : 'My team'}
        <Icon.ChevronD size={10} />
      </button>

      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 32, right: 0, zIndex: 60, minWidth: 300,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
        }}>
          <div style={{
            padding: '8px 12px', fontSize: 10, fontWeight: 700,
            color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em',
            borderBottom: '1px solid var(--border)', background: 'var(--surface-2)',
          }}>
            Team scope
          </div>
          <MenuRadioItem
            selected={!all}
            title="My team"
            sub="Only records that belong to you and your dealteam."
            onClick={() => pick(false)}
          />
          <MenuRadioItem
            selected={all}
            title="All teams"
            badge={{ hue: 'blue', text: 'firm-wide' }}
            sub="Everything across every dealmaker in Atlas, read-only. Reloads the app."
            onClick={() => pick(true)}
            last
          />
        </div>
      )}
    </div>
  );
};

// Notification bell — surfaces the active user's task alerts: overdue, due
// today, and tasks someone else recently assigned to them. Client-computed
// from the tasks API (no notifications table); "seen" state is a per-user
// localStorage set keyed by task id + due date, so a rescheduled or
// recurring-respawned task notifies again while everything else stays quiet.
const NotifBell = ({ activeUser }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const ref = React.useRef(null);

  const seenKey = activeUser ? `atlas.notifSeen.${activeUser.id}` : null;
  const loadSeen = React.useCallback(() => {
    try { return new Set(JSON.parse(localStorage.getItem(seenKey) || '[]')); }
    catch (_) { return new Set(); }
  }, [seenKey]);

  const refresh = React.useCallback(async () => {
    if (!activeUser) { setItems([]); setUnseen(0); return; }
    try {
      const res = await window.AtlasApi.listTasks({ assigned_to: activeUser.id, status: 'open', limit: 100 });
      const tasks = (res && res.items) || [];
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
      const out = [];
      for (const t of tasks) {
        const d = new Date(`${t.due_date}T00:00:00`);
        const assignedByOther = t.created_by && t.created_by !== activeUser.id
          && new Date(t.created_at).getTime() > weekAgo;
        let kind = null;
        if (d < today) kind = 'overdue';
        else if (d.getTime() === today.getTime()) kind = 'due_today';
        else if (assignedByOther) kind = 'assigned';
        if (!kind) continue;
        out.push({
          id: `${kind}:${t.id}:${t.due_date}`,
          kind,
          title: t.title,
          sub: t.engagement_name || null,
          due: t.due_date,
          recurrenceText: t.recurrence_text || null,
          engagementId: t.engagement_id || null,
        });
      }
      const order = { overdue: 0, due_today: 1, assigned: 2 };
      out.sort((a, b) => (order[a.kind] - order[b.kind]) || (a.due < b.due ? -1 : 1));
      const seen = loadSeen();
      setItems(out);
      setUnseen(out.filter((i) => !seen.has(i.id)).length);
    } catch (_) { /* transient — keep whatever we showed last */ }
  }, [activeUser, loadSeen]);

  // Refresh on boot, every 5 minutes, and whenever tasks change elsewhere
  // in the app (⌘K create, Inbox composer, toggling a task done).
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5 * 60 * 1000);
    window.addEventListener('task-created', refresh);
    window.addEventListener('atlas-data-changed', refresh);
    return () => {
      clearInterval(t);
      window.removeEventListener('task-created', refresh);
      window.removeEventListener('atlas-data-changed', refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && seenKey) {
      // Opening the bell acknowledges everything currently listed.
      try {
        const seen = loadSeen();
        items.forEach((i) => seen.add(i.id));
        localStorage.setItem(seenKey, JSON.stringify([...seen].slice(-500)));
      } catch (_) {}
      setUnseen(0);
    }
  };

  const KIND = {
    overdue:   { label: 'Overdue',        hue: 'rose'  },
    due_today: { label: 'Due today',      hue: 'amber' },
    assigned:  { label: 'Assigned to you', hue: 'blue'  },
  };

  const openItem = (i) => {
    setOpen(false);
    if (i.engagementId && window.AtlasNav?.goToDeal) window.AtlasNav.goToDeal(i.engagementId);
    else window.AtlasNav?.switchView?.('inbox');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={toggle} title="Notifications" aria-label="Notifications"
        style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)',
          background: open ? 'var(--surface-2)' : 'var(--surface)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <Icon.Bell size={13} color="var(--text-2)" />
        {unseen > 0 && (
          <span className="num" style={{
            position: 'absolute', top: -4, right: -4, minWidth: 14, height: 14, padding: '0 3px',
            borderRadius: 999, background: 'oklch(0.55 0.18 25)', color: '#fff',
            fontSize: 9, fontWeight: 700, display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', border: '1.5px solid var(--surface)',
          }}>{unseen > 9 ? '9+' : unseen}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 36, width: 340,
          background: 'var(--surface)', border: '1px solid var(--border-strong)',
          borderRadius: 8, boxShadow: 'var(--shadow-lg)', zIndex: 60, overflow: 'hidden',
        }}>
          <div style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)',
            fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center' }}>
            Notifications
            <span style={{ flex: 1 }} />
            <button onClick={() => { setOpen(false); window.AtlasNav?.switchView?.('inbox'); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 11, color: 'var(--accent-text)', padding: 0, fontWeight: 500 }}>
              Open Tasks →
            </button>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {items.length === 0 && (
              <div style={{ padding: '18px 14px', fontSize: 12, color: 'var(--text-3)' }}>
                You're all caught up. 🎉
              </div>
            )}
            {items.map((i, idx) => (
              <button key={i.id} onClick={() => openItem(i)} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', textAlign: 'left',
                padding: '9px 12px', background: 'transparent', border: 'none', cursor: 'pointer',
                borderTop: idx === 0 ? 'none' : '1px solid var(--border)',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ marginTop: 1 }}>
                  <Pill hue={KIND[i.kind].hue} sz="sm">{KIND[i.kind].label}</Pill>
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: 'var(--text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {i.title}
                  </span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-3)' }}>
                    {i.due}{i.recurrenceText ? ` · ↻ ${i.recurrenceText}` : ''}{i.sub ? ` · ${i.sub}` : ''}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Sign out — the TopBar avatar menu and the phone shell's More sheet.
const signOutOfAtlas = () => {
  try { window.AtlasAuth?.clear?.(); } catch (_) {}
  // Clear any legacy honor-system overrides too.
  ['atlas.token', 'atlas.activeUserId', 'atlas.dealGuest'].forEach((k) => localStorage.removeItem(k));
  // The cached UI-prefs mirror is per-person — leaving it would
  // paint this user's column layout for whoever signs in next.
  try { window.clearUiPrefsCache?.(); } catch (_) {}
  // Land on the login screen via ?login=1. Locally AuthGate bypasses
  // login (LOCAL_ENV), so a plain reload would clear the session and
  // drop the user straight back into the app — making Sign out look
  // broken. FORCE_LOGIN shows login in every env; the query is dropped
  // after sign-in (OAuth redirect_to omits it).
  window.location.assign(window.location.pathname + '?login=1');
};

const TopBar = ({ sidebarOpen, onToggleSidebar, onCmdK, onCreate, createHints, showHint, compact = false, mode, forceMock, onSetMock, onReload, activeUser, teamMembers, onSwitchUser, onOpenSettings, theme, onToggleTheme, canSwitchUsers, isAdmin, canSee = () => true }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  return (
    <div style={{
      height: 44, borderBottom: '1px solid var(--border)', background: 'var(--surface)',
      display: 'flex', alignItems: 'center', padding: '0 14px', gap: 10, flexShrink: 0,
    }}>
      {onToggleSidebar && <button onClick={onToggleSidebar} title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'} aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
        style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)', flexShrink: 0 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="16" rx="2" /><line x1="9" y1="4" x2="9" y2="20" />
        </svg>
      </button>}
      {canSee('search') ? <button onClick={onCmdK} style={{
        flex: 1, minWidth: 0, maxWidth: 460, height: 30, border: '1px solid var(--border)',
        background: 'var(--surface-2)', borderRadius: 7, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 7, padding: '0 10px',
        fontSize: 12.5, color: 'var(--text-3)', overflow: 'hidden',
      }}>
        <Icon.Search size={13} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>Search deals, companies, agents…</span>
        {showHint && <span style={{ display: 'inline-flex', gap: 3, flexShrink: 0 }}>
          <kbd style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--surface)', border: '1px solid var(--border)', fontFamily: 'var(--font-mono)' }}>⌘</kbd>
          <kbd style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--surface)', border: '1px solid var(--border)', fontFamily: 'var(--font-mono)' }}>K</kbd>
        </span>}
      </button> : null}
      {onCreate && <CreateMenu onCreate={onCreate} hints={createHints} canSee={canSee} />}
      <div style={{ flex: 1 }} />
      <DataSourcePill mode={mode} forceMock={forceMock} onSetMock={onSetMock} onRetry={onReload} />
      {canSee('deals') && !window.AtlasIsGuest && <ScopeToggle />}
      <button
        onClick={onToggleTheme}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)' }}
      >
        {theme === 'dark' ? <Icon.Sun size={13} /> : <Icon.Moon size={13} />}
      </button>
      {isAdmin && <BackgroundTasksWatcher />}
      <WhatsNew />
      {canSee('inbox') && <NotifBell activeUser={activeUser} />}
      {!compact && <button style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon.Help size={13} color="var(--text-2)" />
      </button>}

      <div ref={menuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          title={activeUser ? `${activeUser.full_name}${activeUser.email ? ' · ' + activeUser.email : ''}` : 'No active user'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 6px 2px 2px',
            border: 'none', background: menuOpen ? 'var(--surface-2)' : 'transparent',
            borderRadius: 14, cursor: 'pointer',
          }}
        >
          {activeUser ? (
            <>
              <Avatar name={activeUser.full_name} size={26} color={userAvatarColor(activeUser.email || activeUser.id)} />
              {!compact && <span style={{ fontSize: 11.5, color: 'var(--text-2)', fontWeight: 500 }}>
                {activeUser.full_name}
              </span>}
              <Icon.ChevronR size={10} color="var(--text-4)" />
            </>
          ) : (
            <Avatar name="?" size={26} color="oklch(0.62 0.008 270)" />
          )}
        </button>

        {menuOpen && (
          <div style={{
            position: 'absolute', right: 0, top: 36, width: 260,
            background: 'var(--surface)', border: '1px solid var(--border-strong)',
            borderRadius: 8, boxShadow: 'var(--shadow-lg)', zIndex: 60,
            padding: 6,
          }}>
            {activeUser && (
              <div style={{ padding: '8px 10px 10px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{activeUser.full_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{activeUser.email}</div>
                {activeUser.role && (
                  <div style={{ marginTop: 4 }}>
                    <Pill hue={activeUser.role === 'admin' ? 'amber' : activeUser.role === 'dealmaker' ? 'blue' : 'slate'} sz="sm">
                      {(activeUser.role || '').replace(/_/g, ' ')}
                    </Pill>
                  </div>
                )}
              </div>
            )}
            {/* Switching users is admin-only. The backend enforces this too
                (visibility.py pins non-admins to their own id regardless of
                the X-Active-User header), but hiding the UI prevents
                non-admins from even trying. */}
            {canSwitchUsers && (
              <>
                <div style={{ padding: '6px 10px 4px', fontSize: 10, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>
                  Switch user
                </div>
                <div style={{ maxHeight: 220, overflow: 'auto' }}>
                  {teamMembers
                    .filter((u) => u.active !== false)
                    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
                    .map((u) => (
                      <button
                        key={u.id}
                        onClick={() => { onSwitchUser(u.id); setMenuOpen(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                          padding: '5px 10px', border: 'none', background: u.id === activeUser?.id ? 'var(--accent-soft)' : 'transparent',
                          cursor: 'pointer', borderRadius: 5, textAlign: 'left', fontSize: 12,
                          color: 'var(--text)',
                        }}
                        onMouseEnter={(e) => { if (u.id !== activeUser?.id) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                        onMouseLeave={(e) => { if (u.id !== activeUser?.id) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <Avatar name={u.full_name} size={20} color={userAvatarColor(u.email || u.id)} />
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.full_name}</span>
                        <span style={{ fontSize: 10, color: 'var(--text-4)' }}>{(u.role || '').slice(0, 5)}</span>
                      </button>
                    ))}
                </div>
              </>
            )}
            <div style={{ borderTop: canSwitchUsers ? '1px solid var(--border)' : 'none', marginTop: 4, paddingTop: 4 }}>
              {canSwitchUsers && (
                <button
                  onClick={() => { onOpenSettings(); setMenuOpen(false); }}
                  style={{
                    width: '100%', padding: '6px 10px', border: 'none',
                    background: 'transparent', cursor: 'pointer', borderRadius: 5,
                    textAlign: 'left', fontSize: 12, color: 'var(--text-2)',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Icon.Settings size={12} /> Manage members in Settings
                </button>
              )}
              {window.AtlasDevice && window.AtlasDevice.touch && (
                <button
                  onClick={() => { setMenuOpen(false); window.setShellOverride?.('phone'); }}
                  style={{
                    width: '100%', padding: '8px 10px', border: 'none',
                    background: 'transparent', cursor: 'pointer', borderRadius: 5,
                    textAlign: 'left', fontSize: 12.5, color: 'var(--text-2)',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <Icon.Phone size={12} /> Phone layout
                </button>
              )}
              <button
                onClick={signOutOfAtlas}
                style={{
                  width: '100%', padding: '6px 10px', border: 'none',
                  background: 'transparent', cursor: 'pointer', borderRadius: 5,
                  textAlign: 'left', fontSize: 12, color: 'var(--text-2)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Icon.ChevronR size={12} /> Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Pull the API's `detail` string out of an apiFetch error ("422 ...: {json}").
const friendlyApiError = (e, fallback) => {
  const m = /:\s*(\{.*\})\s*$/.exec((e && e.message) || '');
  if (m) {
    try {
      const d = JSON.parse(m[1]).detail;
      if (typeof d === 'string' && d) return d;
    } catch (_) { /* not JSON — fall through */ }
  }
  return fallback;
};

// Natural-language task composer — type "remind me every Monday to check on
// Acme and send them a message", the AI parses it into a draft (due date,
// recurrence, priority, company/contact match), and the user confirms before
// it's saved. Parse and create are separate calls so nothing lands unreviewed.
// "@Bor…" opens an entity autocomplete (contacts + companies via /api/search);
// picking one pins the exact record so the parse skips the fuzzy name lookup.
// ── What's new — versioned release notes (releases API) ──────────────────
// Topbar gift button with an unread dot; opening the panel marks everything
// seen (server-side last_seen_at in user_ui_prefs, so it follows the user
// across devices, unlike the notification bell's localStorage seen-state).
const RELEASE_KIND_HUES = { feature: 'green', improvement: 'blue', fix: 'amber' };

// Minimal inline markdown for release bodies: **bold** and [label](link).
// Exposed on window so the Settings editor preview renders identically.
const renderReleaseBody = (text) => {
  const parts = [];
  let rest = String(text || '');
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/;
  let key = 0;
  while (rest) {
    const m = rest.match(re);
    if (!m) { parts.push(rest); break; }
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    if (m[1] !== undefined) parts.push(<strong key={key++}>{m[1]}</strong>);
    else parts.push(<a key={key++} href={m[3]} style={{ color: 'var(--accent)' }}>{m[2]}</a>);
    rest = rest.slice(m.index + m[0].length);
  }
  return parts;
};
window.renderReleaseBody = renderReleaseBody;

// ── Background tasks watcher (admin) ────────────────────────────────────────
// Topbar indicator + drawer over GET /api/background-tasks: credential deep
// backfills, bulk fills, geo enrichment — anything long-running. Polls gently
// while idle, fast while something is live or the drawer is open.
const TASK_STATUS_HUE = {
  running: 'blue', queued: 'amber', done: 'green', completed: 'green',
  error: 'red', failed: 'red', cancelled: 'slate',
};
const TASK_KIND_LABEL = {
  credential_backfill: 'Backfill', bulk_fill: 'Bulk fill', geo_enrichment: 'Geo',
};
const fmtTaskTs = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const BackgroundTasksWatcher = () => {
  const [tasks, setTasks] = useState(null);
  const [open, setOpen] = useState(false);

  const load = React.useCallback(() => {
    window.AtlasApi.listBackgroundTasks().then(setTasks).catch(() => {});
  }, []);

  const liveCount = (tasks || []).filter((t) => t.status === 'running' || t.status === 'queued').length;
  const hasError = (tasks || []).some((t) => t.status === 'error' || t.status === 'failed');

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // 5s with the drawer open, 15s while anything is live, 90s otherwise.
    const ms = open ? 5000 : (liveCount ? 15000 : 90000);
    const t = setInterval(load, ms);
    return () => clearInterval(t);
  }, [load, open, liveCount > 0]);

  return (
    <>
      <button onClick={() => { setOpen(true); load(); }}
        title={liveCount ? `Background tasks — ${liveCount} live` : 'Background tasks'}
        style={{ position: 'relative', width: 28, height: 28, borderRadius: 6,
          border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon.Lightning size={13} color={liveCount ? 'var(--accent)' : 'var(--text-2)'} />
        {(liveCount > 0 || hasError) && (
          <span style={{ position: 'absolute', top: 3, right: 3, width: 7, height: 7,
            borderRadius: 99, background: hasError ? 'var(--rose-fg)' : 'var(--accent)',
            border: '1.5px solid var(--surface)' }} />
        )}
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title="Background tasks" width={560}>
        {tasks === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {tasks !== null && tasks.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Nothing queued or recently finished.</div>
        )}
        {(tasks || []).map((t) => (
          <div key={`${t.kind}-${t.id}`} style={{ borderBottom: '1px solid var(--border)', padding: '9px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <Pill hue="slate" sz="sm">{TASK_KIND_LABEL[t.kind] || t.kind}</Pill>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{t.label}</span>
              <Pill hue={TASK_STATUS_HUE[t.status] || 'slate'} dot sz="sm">{t.status}</Pill>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {t.detail && <span>{t.detail}</span>}
              <span className="num">requested {fmtTaskTs(t.requested_at)}</span>
              {t.finished_at && <span className="num">finished {fmtTaskTs(t.finished_at)}</span>}
            </div>
            {t.error && (
              <div style={{ fontSize: 11.5, color: 'var(--rose-fg)', marginTop: 3 }}>{t.error}</div>
            )}
          </div>
        ))}
      </Drawer>
    </>
  );
};

const WhatsNew = () => {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [releases, setReleases] = useState(null);

  const reloadState = React.useCallback(() => {
    window.AtlasApi.releasesState().then(setState).catch(() => setState(null));
  }, []);
  useEffect(() => {
    reloadState();
    const t = setInterval(reloadState, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [reloadState]);

  const openPanel = async () => {
    setOpen(true);
    try { setReleases(await window.AtlasApi.listReleases()); } catch { setReleases([]); }
    try { await window.AtlasApi.markReleasesSeen(); } catch { /* best-effort */ }
    reloadState();
  };

  const unseen = (state?.unseen_count || 0) > 0;
  return (
    <>
      <button onClick={openPanel} title={unseen ? "What's new — something shipped" : "What's new"}
        style={{ position: 'relative', width: 28, height: 28, borderRadius: 6,
          border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon.Sparkle size={13} color={unseen ? 'var(--accent)' : 'var(--text-2)'} />
        {unseen && (
          <span style={{ position: 'absolute', top: 3, right: 3, width: 7, height: 7,
            borderRadius: 99, background: 'var(--accent)', border: '1.5px solid var(--surface)' }} />
        )}
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title="What's new in Atlas" width={520}>
        {releases === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {releases !== null && releases.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No release notes yet.</div>
        )}
        {(releases || []).map((r) => (
          <div key={r.id} style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span className="num" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-3)' }}>{r.version}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{r.title}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              {(r.items || []).map((it) => (
                <div key={it.id} style={{ borderLeft: '2px solid var(--border)', paddingLeft: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Pill hue={RELEASE_KIND_HUES[it.kind] || 'slate'} sz="sm">{it.kind}</Pill>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{it.title}</span>
                    {it.link && (
                      <a href={it.link} onClick={() => setOpen(false)}
                        style={{ fontSize: 11.5, color: 'var(--accent)' }}>open →</a>
                    )}
                  </div>
                  {it.body && (
                    <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, whiteSpace: 'pre-wrap' }}>
                      {renderReleaseBody(it.body)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </Drawer>
    </>
  );
};

// The natural-language task path (type → /tasks/parse → confirm → create),
// shared by the desktop TaskComposer below and the phone Tasks quick-add
// (src/mobile/tasks.jsx), so both create exactly the same task from the same
// sentence. `inputRef` is the text field — @-mention picks put the caret back.
const useTaskQuickAdd = ({ activeUser, onCreated, inputRef }) => {
  const [text, setText]   = useState('');
  const [busy, setBusy]   = useState(false);
  const [draft, setDraft] = useState(null);      // TaskParseResult from /tasks/parse
  const [companyIdx, setCompanyIdx] = useState(-1);  // index into company_candidates
  const [contactIdx, setContactIdx] = useState(-1);  // index into contact_candidates
  const [error, setError] = useState(null);
  // @-mention entities picked from the dropdown: {kind:'contact'|'company', id, name}
  const [tags, setTags] = useState([]);
  const [mention, setMention]   = useState(null);   // {start, q} — active "@…" token
  const [mentionHits, setMentionHits] = useState([]);
  const [mentionIdx, setMentionIdx]   = useState(0);

  const reset = () => { setDraft(null); setError(null); setCompanyIdx(-1); setContactIdx(-1); };
  const closeMention = () => { setMention(null); setMentionHits([]); setMentionIdx(0); };

  // Active "@token" = last '@' at a word boundary before the caret.
  const trackMention = (value, caret) => {
    const at = value.lastIndexOf('@', caret - 1);
    if (at < 0 || (at > 0 && !/\s/.test(value[at - 1]))) return closeMention();
    const token = value.slice(at + 1, caret);
    if (/[@\n]/.test(token) || token.length > 40) return closeMention();
    setMention({ start: at, q: token });
  };

  // Debounced entity search for the open mention token.
  useEffect(() => {
    if (!mention || mention.q.trim().length < 2) { setMentionHits([]); return undefined; }
    let stale = false;
    const timer = setTimeout(async () => {
      try {
        const r = await window.AtlasApi.search(mention.q.trim(), 12);
        if (stale) return;
        const hits = (r.results || [])
          .filter((x) => x.entity_type === 'contact' || x.entity_type === 'company')
          .slice(0, 6);
        setMentionHits(hits);
        setMentionIdx(0);
      } catch (_) { if (!stale) setMentionHits([]); }
    }, 180);
    return () => { stale = true; clearTimeout(timer); };
  }, [mention && mention.q, mention && mention.start]);  // eslint-disable-line react-hooks/exhaustive-deps

  const pickMention = (hit) => {
    if (!mention) return;
    const caret = inputRef.current ? inputRef.current.selectionStart : text.length;
    const next = text.slice(0, mention.start) + hit.title + text.slice(caret);
    setText(next);
    setTags((prev) => [
      ...prev.filter((t) => !(t.kind === hit.entity_type)),  // one pin per kind — last wins
      { kind: hit.entity_type, id: hit.id, name: hit.title },
    ]);
    closeMention();
    if (draft || error) reset();
    const pos = mention.start + hit.title.length;
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) { el.focus(); el.setSelectionRange(pos, pos); }
    });
  };

  const parse = async () => {
    const q = text.trim();
    if (!q || busy) return;
    closeMention();
    setBusy(true); setError(null); setDraft(null);
    // A pin only holds while its name is still in the text.
    const live = tags.filter((t) => q.toLowerCase().includes(t.name.toLowerCase()));
    if (live.length !== tags.length) setTags(live);
    const contactTag = live.find((t) => t.kind === 'contact');
    const companyTag = live.find((t) => t.kind === 'company');
    try {
      const d = await window.AtlasApi.parseTask(q, {
        tagged_contact_id: contactTag ? contactTag.id : undefined,
        tagged_company_id: companyTag ? companyTag.id : undefined,
      });
      setDraft(d);
      const cIdx = (d.contact_candidates || []).length ? 0 : -1;
      let coIdx = (d.company_candidates || []).length ? 0 : -1;
      if (cIdx >= 0 && coIdx >= 0) {
        // Default the company pick to the chosen contact's employer.
        const j = d.company_candidates.findIndex((x) => x.company_id === d.contact_candidates[0].company_id);
        if (j >= 0) coIdx = j;
      }
      setContactIdx(cIdx);
      setCompanyIdx(coIdx);
    } catch (e) {
      setError(friendlyApiError(e, 'Could not parse that — try rephrasing.'));
    }
    setBusy(false);
  };

  const confirm = async () => {
    if (!draft || busy) return;
    setBusy(true); setError(null);
    const cand = companyIdx >= 0 ? (draft.company_candidates || [])[companyIdx] : null;
    const person = contactIdx >= 0 ? (draft.contact_candidates || [])[contactIdx] : null;
    try {
      await window.AtlasApi.createTask({
        title: draft.title,
        due_date: draft.due_date,
        task_type: draft.task_type,
        priority: draft.priority,
        notes: draft.notes || undefined,
        recurrence: draft.recurrence || undefined,
        recurrence_text: draft.recurrence_text || undefined,
        assigned_to: draft.assigned_to,
        co_assignees: (draft.co_assignees || []).length ? draft.co_assignees : undefined,
        created_by: activeUser?.id || undefined,
        company_id: cand ? cand.company_id : undefined,
        engagement_id: cand?.engagement_id || undefined,
        contact_id: person ? person.contact_id : undefined,
      });
      pushToast({
        type: 'success', title: 'Task created',
        body: draft.recurrence_text
          ? `Repeats ${draft.recurrence_text} · first due ${draft.due_date}`
          : `Due ${draft.due_date}`,
      });
      setText(''); setTags([]); reset();
      onCreated && onCreated();
    } catch (e) {
      setError(friendlyApiError(e, 'Could not create the task.'));
    }
    setBusy(false);
  };

  return {
    text, setText, busy, draft, error, reset,
    companyIdx, setCompanyIdx, contactIdx, setContactIdx,
    mention, mentionHits, mentionIdx, setMentionIdx, trackMention, closeMention, pickMention,
    parse, confirm,
  };
};
window.useTaskQuickAdd = useTaskQuickAdd;

const TaskComposer = ({ activeUser, onCreated }) => {
  const inputRef = useRef(null);
  const {
    text, setText, busy, draft, error, reset,
    companyIdx, setCompanyIdx, contactIdx, setContactIdx,
    mention, mentionHits, mentionIdx, setMentionIdx, trackMention, closeMention, pickMention,
    parse, confirm,
  } = useTaskQuickAdd({ activeUser, onCreated, inputRef });

  const chip = (label, title) => (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
      borderRadius: 999, background: 'var(--surface-2)', border: '1px solid var(--border)',
      fontSize: 11, color: 'var(--text-2)', whiteSpace: 'nowrap',
    }}>{label}</span>
  );

  const candidates = (draft && draft.company_candidates) || [];
  const people     = (draft && draft.contact_candidates) || [];
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 8, padding: '10px 14px', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              trackMention(e.target.value, e.target.selectionStart);
              if (draft || error) reset();
            }}
            onKeyDown={(e) => {
              if (mention && mentionHits.length) {
                if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => (i + 1) % mentionHits.length); return; }
                if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx((i) => (i - 1 + mentionHits.length) % mentionHits.length); return; }
                if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(mentionHits[mentionIdx]); return; }
                if (e.key === 'Escape') { e.preventDefault(); closeMention(); return; }
              }
              if (e.key === 'Enter') { e.preventDefault(); draft ? confirm() : parse(); }
              if (e.key === 'Escape') reset();
            }}
            onBlur={() => setTimeout(closeMention, 150)}
            placeholder='Add a task or reminder… "@" tags a contact or firm — e.g. "remind me to check in with @Boris Pusin in 2 weeks"'
            disabled={busy}
            style={{
              width: '100%', height: 30, padding: '0 10px', fontSize: 12.5, boxSizing: 'border-box',
              border: '1px solid var(--border)', borderRadius: 6,
              background: 'var(--surface)', color: 'var(--text)', outline: 'none',
            }}
          />
          {mention && mentionHits.length > 0 && (
            <div style={{
              position: 'absolute', top: 32, left: 0, minWidth: 260, maxWidth: '100%',
              background: 'var(--surface)', border: '1px solid var(--border-strong)',
              borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.14)', zIndex: 400,
              overflow: 'hidden', padding: 4,
            }}>
              {mentionHits.map((h, i) => (
                <button
                  key={`${h.entity_type}:${h.id}`}
                  onMouseDown={(e) => { e.preventDefault(); pickMention(h); }}
                  onMouseEnter={() => setMentionIdx(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '6px 8px', border: 'none', borderRadius: 5, cursor: 'pointer',
                    background: i === mentionIdx ? 'var(--surface-hover)' : 'transparent',
                    textAlign: 'left', fontSize: 12, color: 'var(--text)',
                  }}>
                  <span aria-hidden="true">{h.entity_type === 'contact' ? '👤' : '🏢'}</span>
                  <span style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.title}</span>
                  {h.subtitle && (
                    <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {h.subtitle}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <Btn size="xs" variant="outline" onClick={parse} disabled={busy || !text.trim()}>
          {busy && !draft ? 'Parsing…' : '✦ Parse'}
        </Btn>
      </div>

      {error && (
        <div style={{ padding: '0 14px 10px', fontSize: 11.5, color: 'var(--rose-fg)' }}>{error}</div>
      )}

      {draft && (
        <div style={{
          margin: '0 14px 10px', padding: '10px 12px', borderRadius: 8,
          border: '1px solid var(--border)', background: 'var(--surface-2)',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{draft.title}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {chip(`📅 ${draft.due_date}`, 'First due date')}
            {draft.recurrence_text && chip(`↻ ${draft.recurrence_text}`, 'Repeats — completing it schedules the next one')}
            {draft.priority !== 'normal' && chip(draft.priority, 'Priority')}
            {chip(draft.task_type.replace(/_/g, ' '), 'Task type')}
            {draft.assigned_to_name && chip(`→ ${draft.assigned_to_name}`, 'Assignee')}
            {(draft.co_assignee_names || []).length > 0 &&
              chip(`👥 with ${draft.co_assignee_names.join(', ')}`, 'Co-assignees — they see this task in their Tasks page too')}
            {people.length > 0 && (
              <Select
                value={String(contactIdx)}
                onChange={(e) => {
                  const idx = Number(e.target.value);
                  setContactIdx(idx);
                  // Follow the person's employer when it's on offer.
                  const p = idx >= 0 ? people[idx] : null;
                  if (p && p.company_id) {
                    const j = candidates.findIndex((c) => c.company_id === p.company_id);
                    if (j >= 0) setCompanyIdx(j);
                  }
                }}
                options={[
                  ...people.map((p, i) => ({
                    value: String(i),
                    label: `👤 ${p.contact_name}${p.company_name ? ` — ${p.company_name}` : ''}`,
                  })),
                  { value: '-1', label: 'No contact link' },
                ]}
                style={{ width: 220, height: 24, fontSize: 11 }}
              />
            )}
            {candidates.length > 0 && (
              <Select
                value={String(companyIdx)}
                onChange={(e) => setCompanyIdx(Number(e.target.value))}
                options={[
                  ...candidates.map((c, i) => ({ value: String(i), label: `🏢 ${c.company_name}` })),
                  { value: '-1', label: 'No company link' },
                ]}
                style={{ width: 200, height: 24, fontSize: 11 }}
              />
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn size="xs" variant="primary" onClick={confirm} disabled={busy}>
              {busy ? 'Creating…' : 'Create task'}
            </Btn>
            <Btn size="xs" variant="ghost" onClick={reset} disabled={busy}>Cancel</Btn>
            <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>Enter to confirm · Esc to discard</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Task form ────────────────────────────────────────────────────────────
// The fields of TaskDrawer, shared with the record composer's Task mode
// (views/record_notes.jsx). The form is controlled: the caller keeps the
// draft (taskFormDraft) and submits it (submitTaskForm); TaskForm only owns
// the link search's own state.
// Map a stored recurrence rule back onto the repeat select.
const taskRepeatOf = (rec) => {
  if (!rec || !rec.freq) return '';
  if (rec.freq === 'daily')   return 'daily';
  if (rec.freq === 'monthly') return 'monthly';
  if (rec.freq === 'weekly')  return (rec.interval || 1) >= 2 ? 'biweekly' : 'weekly';
  return '';
};
// Recurrence is derived from the due date (weekly = that weekday, monthly =
// that day), so it stays a single select.
const taskRecur = (due) => {
  // Backend weekday codes are mon..sun; JS getDay() is sun..sat.
  const dueDate = new Date(`${due || window.localISODate()}T00:00:00`);
  const wd = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][dueDate.getDay()];
  const wdName = dueDate.toLocaleDateString('en-US', { weekday: 'long' });
  return { dueDate, wdName, RECUR: {
    daily:    { rule: { freq: 'daily' },                                text: 'every day' },
    weekly:   { rule: { freq: 'weekly', byweekday: [wd] },              text: `every ${wdName}` },
    biweekly: { rule: { freq: 'weekly', interval: 2, byweekday: [wd] }, text: `every 2 weeks on ${wdName}` },
    monthly:  { rule: { freq: 'monthly', day_of_month: dueDate.getDate() }, text: `monthly on the ${dueDate.getDate()}` },
  } };
};
// A task draft: from `task` when editing, else blank with `initialLinks`
// ({contact, company, engagement}, each {id, name}) seeding a NEW task's links.
const taskFormDraft = ({ activeUser = null, task = null, initialLinks = null } = {}) => {
  const seed = task ? null : (initialLinks || null);
  return {
    title: task?.title || '',
    due: task?.due_date || window.localISODate(),
    repeat: taskRepeatOf(task?.recurrence),          // '' | daily | weekly | biweekly | monthly
    priority: task?.priority || 'normal',
    taskType: task?.task_type || 'follow_up',
    assignee: task?.assigned_to || activeUser?.id || '',
    coAssignees: task?.co_assignees || [],            // member ids
    notes: task?.notes || '',
    // Entity links (the 👤/🏢 the task is about).
    contactLink: task?.contact_id ? { id: task.contact_id, name: task.contact_name || 'Contact' } : ((seed && seed.contact) || null),
    companyLink: task?.company_id ? { id: task.company_id, name: task.company_name || task.engagement_name || 'Company' } : ((seed && seed.company) || null),
    // The deal a new task is about (create only — the edit form doesn't show
    // or change a task's deal).
    engagementLink: (seed && seed.engagement) || null,
  };
};
const taskFormValid = (v) => !!(v && (v.title || '').trim() && v.due && v.assignee);
// Create (no `task`) or PATCH `task`. Throws; the caller toasts.
const submitTaskForm = async (v, { task = null, activeUser = null } = {}) => {
  const rec = taskRecur(v.due).RECUR[v.repeat];
  // Only send link keys that actually changed — an omitted key means
  // "unchanged" to the PATCH (exclude_unset), an explicit null unlinks.
  const linkPatch = {};
  if ((v.contactLink?.id || null) !== (task?.contact_id || null)) {
    linkPatch.contact_id = v.contactLink ? v.contactLink.id : null;
  }
  if ((v.companyLink?.id || null) !== (task?.company_id || null)) {
    linkPatch.company_id = v.companyLink ? v.companyLink.id : null;
  }
  if (task) {
    // Edit — PATCH the changed fields. Recurrence/co-assignees send null /
    // [] explicitly so clearing them sticks.
    await window.AtlasApi.updateTask(task.id, {
      title: v.title.trim(),
      due_date: v.due,
      task_type: v.taskType,
      priority: v.priority,
      assigned_to: v.assignee,
      co_assignees: v.coAssignees.filter((id) => id !== v.assignee),
      notes: v.notes.trim() || null,
      recurrence: rec ? rec.rule : null,
      recurrence_text: rec ? rec.text : null,
      ...linkPatch,
    });
  } else {
    await window.AtlasApi.createTask({
      title: v.title.trim(),
      due_date: v.due,
      task_type: v.taskType,
      priority: v.priority,
      assigned_to: v.assignee,
      co_assignees: v.coAssignees.length ? v.coAssignees.filter((id) => id !== v.assignee) : undefined,
      created_by: activeUser?.id || undefined,
      notes: v.notes.trim() || undefined,
      recurrence: rec ? rec.rule : undefined,
      recurrence_text: rec ? rec.text : undefined,
      contact_id: v.contactLink ? v.contactLink.id : undefined,
      company_id: v.companyLink ? v.companyLink.id : undefined,
      engagement_id: v.engagementLink ? v.engagementLink.id : undefined,
    });
  }
  return { mode: task ? 'update' : 'create', recurrenceText: rec ? rec.text : null };
};
// The fields. `onChange` gets an updater ((prev) => next), so a React state
// setter works directly. `quickPicks` adds the follow-up chips under the due
// date; `compact` (the record composer, inline and phone sheet) drops the
// hints and the Notes placeholder, shortens Notes and skips the autofocus.
const TaskForm = ({ value: v, onChange, members, activeUser, isEdit = false, quickPicks = false, compact = false, onSubmit }) => {
  const [linkQ, setLinkQ]       = React.useState('');
  const [linkHits, setLinkHits] = React.useState([]);

  React.useEffect(() => {
    const q = linkQ.trim();
    if (q.length < 2) { setLinkHits([]); return undefined; }
    let stale = false;
    const timer = setTimeout(async () => {
      try {
        const r = await window.AtlasApi.search(q, 12);
        if (!stale) {
          setLinkHits((r.results || [])
            .filter((x) => x.entity_type === 'contact' || x.entity_type === 'company')
            .slice(0, 5));
        }
      } catch (_) { if (!stale) setLinkHits([]); }
    }, 180);
    return () => { stale = true; clearTimeout(timer); };
  }, [linkQ]);

  const set = (patch) => onChange((prev) => ({ ...prev, ...patch }));
  const pickLink = (h) => {
    if (h.entity_type === 'contact') set({ contactLink: { id: h.id, name: h.title } });
    else set({ companyLink: { id: h.id, name: h.title } });
    setLinkQ(''); setLinkHits([]);
  };
  const { dueDate, wdName } = taskRecur(v.due);
  const hint = (s) => (compact ? undefined : s);
  const dv = window.AtlasDevice || {};
  const pickH = dv.touch ? 36 : null;

  return (
    <>
      <Field label="Title">
        {/* Editing on a touch device: no autofocus — the keyboard would
            cover the form you opened to change a date or an assignee. The
            inline composer focuses its own field when it opens. */}
        <TextInput value={v.title} onChange={(e) => set({ title: e.target.value })}
          placeholder="Check on Acme and send them a message" autoFocus={!compact && (!isEdit || !dv.touch)}
          onKeyDown={(e) => { if (e.key === 'Enter' && onSubmit) onSubmit(); }} />
      </Field>
      <Field label="Due date">
        <TextInput type="date" value={v.due} onChange={(e) => set({ due: e.target.value })} />
      </Field>
      {quickPicks && (
        <div style={{ display: 'flex', alignItems: 'center', gap: pickH ? 8 : 6, flexWrap: 'wrap', marginTop: -6, marginBottom: 12 }}>
          {(window.REMINDER_QUICK_PICKS || []).map((p) => {
            const due = new Date();
            due.setDate(due.getDate() + p.days);
            const iso = window.localISODate(due);
            const on = v.due === iso;
            return (
              <button key={p.label} type="button" onClick={() => set({ due: iso })}
                style={{
                  padding: pickH ? '0 14px' : '3px 9px', borderRadius: 999, fontSize: pickH ? (dv.phone ? 14 : 13) : 11, cursor: 'pointer',
                  ...(pickH ? { height: pickH } : null),
                  border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                  background: on ? 'var(--accent)' : 'var(--surface)',
                  color: on ? '#fff' : 'var(--text-2)', fontWeight: on ? 600 : 400,
                }}>
                {p.label}
              </button>
            );
          })}
        </div>
      )}
      <Field label="Repeats" hint={hint('Completing an occurrence schedules the next one automatically.')}>
        <Select value={v.repeat} onChange={(e) => set({ repeat: e.target.value })}
          options={[
            { value: '',         label: 'Does not repeat' },
            { value: 'daily',    label: 'Every day' },
            { value: 'weekly',   label: `Every week on ${wdName}` },
            { value: 'biweekly', label: `Every 2 weeks on ${wdName}` },
            { value: 'monthly',  label: `Monthly on the ${dueDate.getDate()}` },
          ]}
        />
      </Field>
      <Field label="Priority">
        <Select value={v.priority} onChange={(e) => set({ priority: e.target.value })}
          options={['urgent', 'high', 'normal', 'low'].map((p) => ({ value: p, label: p }))}
        />
      </Field>
      <Field label="Type">
        <Select value={v.taskType} onChange={(e) => set({ taskType: e.target.value })}
          options={[
            ['follow_up', 'Follow up'], ['schedule_call', 'Schedule call'],
            ['send_email', 'Send email'], ['review_draft', 'Review draft'],
            ['data_request', 'Data request'], ['nda_send', 'Send NDA'],
            ['blast_prep', 'Blast prep'], ['escalation', 'Escalation'], ['other', 'Other'],
          ].map(([val, l]) => ({ value: val, label: l }))}
        />
      </Field>
      <Field label="Linked to" hint={hint('Connect the task to a contact and/or firm — it shows on the task and links back to their page.')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Phone: the chips' ✕ boxes reach ~12px past a chip, so wrapped rows and the
              search box below keep that much room (a tap on the search never unlinks). */}
          {(v.contactLink || v.companyLink || v.engagementLink) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', ...(dv.phone ? { rowGap: 14, marginBottom: 8 } : null) }}>
              {[['engagement', v.engagementLink, '🤝', 'engagementLink'], ['contact', v.contactLink, '👤', 'contactLink'], ['company', v.companyLink, '🏢', 'companyLink']]
                .filter(([, link]) => link)
                .map(([kind, link, icon, field]) => (
                  <span key={kind} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px',
                    borderRadius: 999, background: 'var(--surface-2)', border: '1px solid var(--border)',
                    fontSize: 11.5, color: 'var(--text-2)',
                  }}>
                    {icon} {link.name}
                    <button onClick={() => set({ [field]: null })}
                      title="Unlink" style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                        color: 'var(--text-3)', fontSize: 11, padding: 0, lineHeight: 1, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}✕</button>
                  </span>
                ))}
            </div>
          )}
          <TextInput value={linkQ} onChange={(e) => setLinkQ(e.target.value)}
            placeholder='Search contacts & firms — e.g. "Boris" or "Acme"' />
          {linkHits.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              {linkHits.map((h) => (
                <button key={`${h.entity_type}:${h.id}`} onClick={() => pickLink(h)} style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '6px 10px', border: 'none', cursor: 'pointer',
                  background: 'transparent', textAlign: 'left', fontSize: 12, color: 'var(--text)',
                }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span aria-hidden="true">{h.entity_type === 'contact' ? '👤' : '🏢'}</span>
                  <span style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.title}</span>
                  {h.subtitle && (
                    <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {h.subtitle}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </Field>
      <Field label="Assignee">
        <Select value={v.assignee} onChange={(e) => set({ assignee: e.target.value })}
          options={(members || []).filter((m) => m.active !== false)
            .map((m) => ({ value: m.id, label: m.full_name }))}
        />
      </Field>
      <Field label="Co-assignees" hint={hint('They see the task in their Tasks page too, and anyone on it can complete it.')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {v.coAssignees.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {v.coAssignees.map((id) => {
                const m = (members || []).find((x) => x.id === id);
                return (
                  <span key={id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px',
                    borderRadius: 999, background: 'var(--surface-2)', border: '1px solid var(--border)',
                    fontSize: 11.5, color: 'var(--text-2)',
                  }}>
                    👤 {m ? m.full_name : '?'}
                    <button onClick={() => onChange((prev) => ({ ...prev, coAssignees: prev.coAssignees.filter((x) => x !== id) }))}
                      title="Remove" style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                        color: 'var(--text-3)', fontSize: 11, padding: 0, lineHeight: 1, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}✕</button>
                  </span>
                );
              })}
            </div>
          )}
          <Select value="" placeholder="Add co-assignee…"
            onChange={(e) => {
              const id = e.target.value;
              if (id) onChange((prev) => (prev.coAssignees.includes(id) ? prev : { ...prev, coAssignees: [...prev.coAssignees, id] }));
            }}
            options={(members || [])
              .filter((m) => m.active !== false && m.id !== v.assignee && !v.coAssignees.includes(m.id))
              .map((m) => ({ value: m.id, label: m.full_name }))}
          />
        </div>
      </Field>
      <Field label="Notes" hint={hint('Longer notes and email drafts — reviewers read this on the task row.')}>
        {/* Multi-line on purpose: this is where email drafts tied to a task
            live, so a reviewer gets them in context. Enter inserts a newline —
            only the Title input submits on Enter. */}
        <textarea
          value={v.notes}
          onChange={(e) => set({ notes: e.target.value })}
          rows={compact ? 3 : 7}
          placeholder={compact ? undefined : 'Draft the email here — Kellen reviews it before it goes out.'}
          style={{
            width: '100%', resize: 'vertical', padding: '6px 8px', fontSize: 12.5,
            lineHeight: 1.5, overflow: 'auto', whiteSpace: 'pre-wrap',
            border: '1px solid var(--border)', borderRadius: 5, fontFamily: 'inherit',
            background: 'var(--surface)', color: 'var(--text)',
          }}
        />
      </Field>
    </>
  );
};

// Manual task create/edit — same Drawer format as NewCompanyDrawer / the
// other "+ New X" pages, for when you'd rather fill fields than phrase it in
// natural language. Pass `task` to edit an existing one instead (PATCH +
// optional delete). `initialLinks` ({contact, company, engagement}, each
// {id, name}) seeds a NEW task's links — "+ New → Task" on a deal / company /
// contact page. The fields are TaskForm (shared with the record composer).
const TaskDrawer = ({ activeUser, members, task, onClose, onCreated, initialLinks }) => {
  const [draft, setDraft] = React.useState(() => taskFormDraft({ activeUser, task, initialLinks }));
  const [busy, setBusy]   = React.useState(false);

  const submit = async () => {
    if (!taskFormValid(draft) || busy) return;
    setBusy(true);
    try {
      const r = await submitTaskForm(draft, { task, activeUser });
      if (task) {
        pushToast({ type: 'success', title: 'Task updated', body: `Due ${draft.due}` });
      } else {
        pushToast({
          type: 'success', title: 'Task created',
          body: r.recurrenceText ? `Repeats ${r.recurrenceText} · first due ${draft.due}` : `Due ${draft.due}`,
        });
      }
      onCreated && onCreated();
      onClose();
    } catch (e) {
      pushToast({ type: 'error', title: task ? 'Save failed' : 'Create failed', body: friendlyApiError(e, e.message) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!task || busy) return;
    const ok = await showConfirm({
      title: 'Delete this task?',
      body: 'It disappears for every assignee. Mark it done instead if the work happened.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await window.AtlasApi.deleteTask(task.id);
      pushToast({ type: 'success', title: 'Task deleted' });
      onCreated && onCreated();
      onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Delete failed', body: friendlyApiError(e, e.message) });
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={task ? 'Edit task' : 'New task'}
      footer={(
        <>
          {task && (
            <Btn size="sm" variant="ghost" onClick={remove} disabled={busy}
              style={{ color: 'var(--rose-fg)', marginRight: 'auto' }}>Delete</Btn>
          )}
          <Btn size="sm" variant="outline" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={submit} disabled={busy || !taskFormValid(draft)}>
            {busy ? 'Saving…' : task ? 'Save changes' : 'Create task'}
          </Btn>
        </>
      )}
    >
      <TaskForm value={draft} onChange={setDraft} members={members} activeUser={activeUser} isEdit={!!task} onSubmit={submit} />
    </Drawer>
  );
};

// Tasks (nav label; the view key stays 'inbox' so old #/inbox links keep
// working) — aggregates "things that need my attention" across:
//   - open tasks assigned to the active user
//   - engagement alerts (exited / on hold gone quiet / active deals stalled)
//   - recent agent workspace activity
const InboxView = ({ activeUser, setView, setDealId }) => {
  // Mine = tasks I own or am co-assigned on + my deal alerts. Team = every
  // task assigned to someone I can see (visible_user_ids: my teams; admins
  // see all) + the team's alerts. Associates own no deals, so for them
  // Mine is their co-assigned list and Team is where the book lives.
  const [tab, setTab] = useState('mine');
  // Per-user opt-in/out of the automatic task rules — a drawer off the
  // header gear, not a footer in the suggestions list.
  const [rulesOpen, setRulesOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState(null);
  const [tasks,      setTasks]      = useState(null);
  const [taskFilter, setTaskFilter] = useState('open');  // open | overdue | done
  const [taskKind, setTaskKind]     = useState('all');   // all | marketing | deals
  const [taskSource, setTaskSource] = useState('all');   // all | manual | automation_rule
  // Narrowing filters inside "my tasks" (the list stays pinned to the
  // signed-in user — these only narrow it, they never widen visibility).
  // { priorities: [], assignee: '', coAssignee: '' }
  const [taskFilters, setTaskFilters] = useState({ priorities: [], assignee: '', coAssignee: '' });
  const [taskFilterOpen, setTaskFilterOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [editingTask, setEditingTask]   = useState(null);  // task object being edited
  const [alertDeals, setAlertDeals] = useState(null);
  // Bulk triage of engagement alerts.
  const [selectedAlerts, setSelectedAlerts] = useState(() => new Set());
  const [members, setMembers] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  // iPad: the tasks | agent-activity grid stacks into one column when its
  // container is narrow (portrait, Split View, sidebar open) — measured on
  // the container, not the window. Desktop never measures and never stacks.
  const device = window.useDevice();
  const gridRef = useRef(null);
  const [gridW, setGridW] = useState(0);
  useEffect(() => {
    const el = gridRef.current;
    if (!device.tablet || !el || typeof ResizeObserver === 'undefined') { setGridW(0); return undefined; }
    const ro = new ResizeObserver((entries) => setGridW(Math.round(entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [device.tablet]);
  const stackGrid = device.tablet && gridW > 0 && gridW < 900;

  useEffect(() => {
    window.AtlasData.loadTeamMembers()
      .then((m) => setMembers(Array.isArray(m) ? m : []))
      .catch(() => setMembers([]));
  }, []);
  // Same roster the task drawer's assignee picker uses, shaped for the
  // filter popover's person selects.
  const memberOptions = React.useMemo(() => (members || [])
    .filter((m) => m.active !== false)
    .map((m) => ({ value: m.id, label: m.full_name || m.email })), [members]);

  const reloadTasks = React.useCallback(async () => {
    if (!activeUser) { setTasks([]); return; }
    // Team tab drops the assignee pin and scopes by visibility instead —
    // the tab used to flip only the alerts below, so an associate clicking
    // "Team" saw the header count change and the list stay put.
    const params = tab === 'all'
      ? { my_team: true, limit: 100 }
      : { assigned_to: activeUser.id, limit: 100 };
    if (taskKind !== 'all')       params.kind = taskKind;
    if (taskSource !== 'all')     params.source = taskSource;
    if (taskFilter === 'open')    params.status = 'open';
    if (taskFilter === 'overdue') { params.overdue = true; }
    if (taskFilter === 'done')    params.status = 'completed';
    // Repeated ?priority= params — qs() in api.jsx expands arrays.
    if ((taskFilters.priorities || []).length) params.priority = taskFilters.priorities;
    if (taskFilters.assignee)   params.assignee_id = taskFilters.assignee;
    if (taskFilters.coAssignee) params.co_assignee_id = taskFilters.coAssignee;
    try {
      const res = await window.AtlasApi.listTasks(params);
      setTasks((res && res.items) || []);
    } catch {
      setTasks([]);
    }
  }, [activeUser, tab, taskFilter, taskKind, taskSource, taskFilters]);

  useEffect(() => { reloadTasks(); }, [reloadTasks]);
  // Tasks created elsewhere (⌘K quick-create) land in this list too.
  useEffect(() => {
    const refresh = () => reloadTasks();
    window.addEventListener('task-created', refresh);
    return () => window.removeEventListener('task-created', refresh);
  }, [reloadTasks]);
  // Agent activity follows the tab: Mine = my workspaces, Team = the
  // people I can see. It used to list the whole company's for everyone.
  useEffect(() => {
    setWorkspaces(null);
    window.AtlasApi.agentWorkspaces({ scope: tab === 'all' ? 'team' : 'mine' })
      .then((ws) => setWorkspaces(Array.isArray(ws) ? ws : []))
      .catch(() => setWorkspaces([]));
  }, [tab]);

  // Open-task totals for the tab pills, independent of the list filters
  // (the pills used to show the engagement-alert counts, which read as
  // "Team 1000" next to a seven-row task list).
  const [taskCounts, setTaskCounts] = useState({ mine: null, all: null });
  const reloadTaskCounts = React.useCallback(async () => {
    if (!activeUser) return;
    const total = (p) => window.AtlasApi.listTasks({ ...p, status: 'open', limit: 1 })
      .then((r) => (r && typeof r.total === 'number') ? r.total : null)
      .catch(() => null);
    const [mine, all] = await Promise.all([
      total({ assigned_to: activeUser.id }),
      total({ my_team: true }),
    ]);
    setTaskCounts({ mine, all });
  }, [activeUser]);
  useEffect(() => { reloadTaskCounts(); }, [reloadTaskCounts]);
  useEffect(() => {
    window.addEventListener('task-created', reloadTaskCounts);
    return () => window.removeEventListener('task-created', reloadTaskCounts);
  }, [reloadTaskCounts]);

  // Automations: pending AI-suggested tasks (confirm/dismiss) + rule registry.
  const [proposals, setProposals] = useState(null);
  const [taskRules, setTaskRules] = useState([]);
  const reloadProposals = React.useCallback(async () => {
    try { setProposals(await window.AtlasApi.listTaskProposals()); } catch { setProposals([]); }
  }, []);
  useEffect(() => { reloadProposals(); }, [reloadProposals]);
  useEffect(() => {
    window.AtlasApi.listTaskRules().then((r) => setTaskRules(Array.isArray(r) ? r : [])).catch(() => setTaskRules([]));
  }, []);
  const actProposal = async (p, action) => {
    try {
      await window.AtlasApi.actTaskProposal(p.id, action);
      reloadProposals();
      if (action === 'accept') reloadTasks();
    } catch (e) {
      console.error('proposal action failed', e);
    }
  };
  // Atlas Inbox: forwarded emails awaiting a filing decision — the same
  // options the Slack buttons carry, for people who live in the app.
  const [inboxPending, setInboxPending] = useState([]);
  const reloadInboxPending = React.useCallback(async () => {
    try {
      const rows = await window.AtlasApi.listInboxPending();
      setInboxPending(Array.isArray(rows) ? rows : []);
    } catch { setInboxPending([]); }
  }, []);
  useEffect(() => { reloadInboxPending(); }, [reloadInboxPending]);
  const actInbox = async (s, opt) => {
    try {
      const res = await window.AtlasApi.actInboxSubmission(s.id, opt.verb, opt.arg);
      if (res && res.result) pushToast(res.result);
      reloadInboxPending();
      reloadProposals(); // an executed forward may have added a suggested task
    } catch (e) {
      console.error('inbox action failed', e);
    }
  };
  const toggleRule = async (r) => {
    try {
      const next = await window.AtlasApi.setTaskRulePref(r.key, !r.disabled);
      setTaskRules(Array.isArray(next) ? next : taskRules);
    } catch (e) {
      console.error('rule pref failed', e);
    }
  };

  // Fetch only the alert-eligible engagements: attention_only applies the
  // shared "needs attention" predicate server-side (on hold quiet 6mo+ /
  // active + non-terminal quiet 90d+; exited deals never qualify), quietest
  // first. The old plain
  // limit=1000 pull was ordered newest-first, so the stalest deals — the
  // whole point of the feed — never made the cut. The client-side filter
  // below stays as a no-op safety net.
  const reloadAlerts = React.useCallback(async () => {
    setAlertDeals(null);
    try {
      const res = await window.AtlasApi.listEngagements({
        my_team: true,
        include_archived: false,
        attention_only: true,
        limit: 1000,
      });
      const items = ((res && res.items) || []).map(window.AtlasData.__adaptEngagement);
      setAlertDeals(items);
    } catch {
      setAlertDeals([]);
    }
  }, []);
  useEffect(() => { reloadAlerts(); }, [reloadAlerts]);
  useEffect(() => {
    const refresh = () => reloadAlerts();
    window.addEventListener('deal-updated', refresh);
    return () => window.removeEventListener('deal-updated', refresh);
  }, [reloadAlerts]);

  // Checking a task off is optimistic + lingers with inline feedback (see
  // useTaskToggle in primitives.jsx) — reloading the list immediately used
  // to make a completed recurring task look like nothing happened, because
  // the spawned next occurrence replaced it in place.
  const reloadTasksAndCounts = React.useCallback(() => { reloadTasks(); reloadTaskCounts(); }, [reloadTasks, reloadTaskCounts]);
  const taskToggle = window.useTaskToggle(reloadTasksAndCounts);

  // Subtasks: one level deep, inline under their parent. Expansion is
  // per-parent UI state; drafts hold the "Add a subtask…" input per parent.
  const [expandedTasks, setExpandedTasks] = useState({});
  const [subDrafts, setSubDrafts] = useState({});
  const toggleExpand = (id) => setExpandedTasks((m) => ({ ...m, [id]: !m[id] }));
  // Notes (incl. email drafts) are readable from the row without opening the
  // edit drawer. Collapsed by default so list density is unchanged.
  const [expandedNotes, setExpandedNotes] = useState({});
  const toggleNotes = (id) => setExpandedNotes((m) => ({ ...m, [id]: !m[id] }));
  const addSubtask = async (parent) => {
    const title = (subDrafts[parent.id] || '').trim();
    if (!title) return;
    try {
      await window.AtlasApi.createTask({
        title,
        parent_task_id: parent.id,
        assigned_to: parent.assigned_to,
        due_date: parent.due_date,
        created_by: activeUser?.id || undefined,
      });
      setSubDrafts((d) => ({ ...d, [parent.id]: '' }));
      reloadTasks();
    } catch (e) {
      console.error('subtask create failed', e);
    }
  };

  // Hygiene loop: one-tap follow-up when a task completes (the Pipedrive
  // nudge — keeps "every live deal has a next step" true with zero engine)
  // and snooze presets that just move due_date (the Close model).
  const addDaysIso = (days) => {
    const d = new Date(); d.setDate(d.getDate() + days);
    return window.localISODate(d);
  };
  // Row-leading due date: "Today" / "Tmrw" / "Sep 25" / "Sep 25 '27" — short
  // enough for a fixed 56px column so every title starts at the same x.
  const fmtDueShort = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tmrw';
    const md = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return d.getFullYear() === today.getFullYear() ? md : `${md} '${String(d.getFullYear()).slice(2)}`;
  };
  const [snoozingId, setSnoozingId] = useState(null);
  const [followedUp, setFollowedUp] = useState({});  // task id → follow-up due date
  const createFollowUp = async (t, days) => {
    const due = addDaysIso(days);
    try {
      await window.AtlasApi.createTask({
        title: t.title,
        company_id: t.company_id || undefined,
        engagement_id: t.engagement_id || undefined,
        contact_id: t.contact_id || undefined,
        assigned_to: t.assigned_to,
        created_by: activeUser?.id || undefined,
        task_type: t.task_type,
        priority: t.priority,
        due_date: due,
      });
      setFollowedUp((m) => ({ ...m, [t.id]: due }));
      pushToast({ type: 'success', title: 'Follow-up scheduled', body: `Due ${window.fmtTaskDue(due)}` });
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not schedule the follow-up', body: e.message });
    }
  };
  const snoozeTask = async (t, days) => {
    setSnoozingId(null);
    try {
      await window.AtlasApi.updateTask(t.id, { due_date: addDaysIso(days) });
      reloadTasks();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not snooze the task', body: e.message });
    }
  };
  const snoozeAllOverdue = async () => {
    const today = addDaysIso(0);
    const overdueOpen = (tasks || []).filter(
      (t) => t.status !== 'completed' && new Date(`${t.due_date}T23:59:59`) < new Date(),
    );
    if (!overdueOpen.length) return;
    await Promise.allSettled(overdueOpen.map((t) => window.AtlasApi.updateTask(t.id, { due_date: today })));
    pushToast({ type: 'success', title: 'Overdue rescheduled', body: `${overdueOpen.length} task(s) moved to today` });
    reloadTasks();
  };

  // "Later" (due beyond this week) is collapsed by default so the open list
  // reads as "what needs me now" — future recurring occurrences live here.
  const [showLater, setShowLater] = useState(() => {
    try { return localStorage.getItem('atlas:tasks-show-later') === '1'; } catch { return false; }
  });
  const toggleLater = () => setShowLater((v) => {
    const next = !v;
    try { localStorage.setItem('atlas:tasks-show-later', next ? '1' : '0'); } catch { /* private mode */ }
    return next;
  });

  // Open tasks get grouped by horizon (Overdue / Today / This week / Later)
  // so "what's coming up" reads at a glance; other filters stay flat.
  const taskGroups = useMemo(() => {
    const list = tasks || [];
    if (!list.length) return [];
    if (taskFilter !== 'open') return [[null, list]];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const week = new Date(today); week.setDate(week.getDate() + 7);
    const buckets = { Overdue: [], Today: [], 'This week': [], Later: [] };
    for (const t of list) {
      const d = new Date(`${t.due_date}T00:00:00`);
      if (d < today) buckets.Overdue.push(t);
      else if (d.getTime() === today.getTime()) buckets.Today.push(t);
      else if (d < week) buckets['This week'].push(t);
      else buckets.Later.push(t);
    }
    return Object.entries(buckets).filter(([, l]) => l.length);
  }, [tasks, taskFilter]);

  const myName = activeUser?.full_name;
  // Mirrors the server's _attention_filter (routes/engagements.py): active
  // deals alert after 90d without a touch, on-hold ones after 6 months.
  const ACTIVE_STALE_DAYS = 90;
  const ON_HOLD_STALE_DAYS = 180;
  const dayMs = 1000 * 60 * 60 * 24;

  const alerts = useMemo(() => {
    if (!alertDeals) return [];
    const now = Date.now();
    // "Last touch" = most recent of the last stage move and the newest
    // linked note — the same activity anchor the server filters on.
    const touchOf = (d) => {
      const stamps = [d._raw?.last_stage_update, d.lastActivityAt]
        .filter(Boolean)
        .map((iso) => new Date(iso).getTime())
        .filter((t) => !Number.isNaN(t));
      return stamps.length ? Math.max(...stamps) : null;
    };
    return alertDeals
      .map((d) => {
        const t = touchOf(d);
        return { d, lastTouch: t, age: t != null ? (now - t) / dayMs : null };
      })
      .filter(({ d, age }) => !d.stale && age != null && age >= (d.onHold ? ON_HOLD_STALE_DAYS : ACTIVE_STALE_DAYS))
      .sort((a, b) => (b.age || 0) - (a.age || 0));
  }, [alertDeals]);

  const filtered = useMemo(() => {
    if (tab === 'mine') return alerts.filter(({ d }) => d.owner === myName);
    return alerts;
  }, [alerts, tab, myName]);

  // ── Bulk triage ──────────────────────────────────────────────────────
  // Selection is keyed by engagement id. Reset when the tab changes (the
  // visible set changes) or after a bulk action completes.
  useEffect(() => { setSelectedAlerts(new Set()); }, [tab]);

  const visibleIds = useMemo(() => filtered.map(({ d }) => d.id), [filtered]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedAlerts.has(id));

  const toggleAlert = (id) => setSelectedAlerts((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllAlerts = () => setSelectedAlerts(
    allVisibleSelected ? new Set() : new Set(visibleIds)
  );

  // Apply a PATCH to every selected engagement, then refresh. `patch` is
  // either a static object (archive) or a function of the id (reassign).
  const applyBulk = async (label, patch) => {
    const ids = [...selectedAlerts];
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);
    let ok = 0, failed = 0;
    for (const id of ids) {
      try { await window.AtlasApi.updateEngagement(id, typeof patch === 'function' ? patch(id) : patch); ok++; }
      catch { failed++; }
    }
    setBulkBusy(false);
    setSelectedAlerts(new Set());
    reloadAlerts();
    window.dispatchEvent(new CustomEvent('atlas-data-changed'));
    pushToast({
      type: failed ? 'warning' : 'success',
      title: `${label}: ${ok} updated${failed ? `, ${failed} failed` : ''}`,
    });
  };

  const bulkArchive = async () => {
    const n = selectedAlerts.size;
    const ok = await showConfirm({
      title: `Archive ${n} engagement${n === 1 ? '' : 's'}?`,
      body: 'They\'ll drop off the active pipeline and these alerts. You can un-archive any of them from the deal page.',
      confirmLabel: `Archive ${n}`,
      danger: true,
    });
    if (ok) applyBulk('Archived', { is_archived: true, archived_reason: 'manual' });
  };

  const bulkReassign = (memberId) => {
    if (!memberId) return;
    const m = members.find((x) => x.id === memberId);
    applyBulk(`Reassigned to ${m?.full_name || 'dealmaker'}`, { dealmaker_id: memberId });
  };

  // Recent agent activity — last 10 workspaces touched
  const recentWorkspaces = useMemo(() => {
    if (!workspaces) return [];
    const sorted = [...workspaces].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    return sorted.slice(0, 10);
  }, [workspaces]);

  // Alerts come back capped at 1000 rows — say so rather than print a
  // suspiciously round number.
  const ALERT_FETCH_CAP = 1000;
  const alertsCapped = (alertDeals || []).length >= ALERT_FETCH_CAP;
  const fmtAlertCount = (n) => (alertsCapped && n >= ALERT_FETCH_CAP ? `${ALERT_FETCH_CAP}+` : String(n));
  const counts = {
    all:  alerts.length,
    mine: alerts.filter(({ d }) => d.owner === myName).length,
  };

  const openTaskCount = (tasks || []).filter((t) => t.status === 'open').length;
  const fmtCount = (n) => (n == null ? '…' : String(n));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Tasks']}
        title="Tasks"
        meta={`${openTaskCount} open ${tab === 'all' ? 'team' : 'my'} tasks · ${fmtAlertCount(filtered.length)} engagement alerts`}
        actions={(
          <Btn size="sm" variant="outline" icon={<Icon.Settings size={12} />}
            onClick={() => setRulesOpen(true)}>
            Automatic tasks
          </Btn>
        )}
      />
      <Drawer open={rulesOpen} onClose={() => setRulesOpen(false)} title="Automatic tasks" width={520}>
        {taskRules.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
        )}
        {taskRules.map((r) => (
          <div key={r.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: r.disabled ? 'var(--text-3)' : 'var(--text)' }}>
                {r.label}
                {!r.active && (
                  <span title="Not switched on for the company yet — your choice is saved for when it is" style={{
                    marginLeft: 6, fontSize: 10, fontWeight: 500, color: 'var(--text-4)',
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    borderRadius: 999, padding: '1px 6px', verticalAlign: 'middle',
                  }}>coming soon</span>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{r.description}</div>
            </div>
            <button onClick={() => toggleRule(r)} role="switch" aria-checked={!r.disabled}
              title={r.disabled ? 'Off for you — click to turn on' : 'On for you — click to turn off'}
              style={{
                width: 32, height: 18, borderRadius: 999, padding: 1.5, flexShrink: 0, marginTop: 1,
                background: r.disabled ? 'oklch(0.85 0.005 270)' : 'oklch(0.55 0.12 155)',
                border: 'none', cursor: 'pointer', transition: 'background .15s',
              }}>
              <span style={{
                display: 'block', width: 15, height: 15, borderRadius: 999, background: 'var(--surface)',
                transform: r.disabled ? 'translateX(0)' : 'translateX(14px)', transition: 'transform .15s',
                boxShadow: '0 1px 2px rgba(0,0,0,.15)',
              }} />
            </button>
          </div>
        ))}
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 12 }}>
          Turning a rule off stops new tasks from it; tasks it already created stay until you complete or delete them.
        </div>
      </Drawer>
      <div style={{ display: 'flex', alignItems: 'center', gap: 1, padding: '0 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)', height: 36 }}>
        {[['mine', 'Mine', fmtCount(taskCounts.mine)], ['all', 'Team', fmtCount(taskCounts.all)]].map(([k, l, n]) => {
          const a = tab === k;
          return (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: '0 10px', height: 36, border: 'none', background: 'transparent',
              fontSize: 12.5, fontWeight: a ? 600 : 500, cursor: 'pointer',
              color: a ? 'var(--text)' : 'var(--text-3)',
              borderBottom: a ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {l}<span style={{ fontSize: 10.5, color: 'var(--text-4)' }} className="num">{n}</span>
            </button>
          );
        })}
      </div>

      <div ref={gridRef} style={{ flex: 1, overflow: 'auto', padding: 16, display: 'grid', gridTemplateColumns: stackGrid ? 'minmax(0, 1fr)' : 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 14, alignContent: stackGrid ? 'start' : undefined }}>
        {/* Left column: Tasks (top) + Engagement alerts (below) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        <Section
          title={`${tab === 'all' ? 'Team tasks' : 'My tasks'} · ${(tasks || []).length}`}
          action={(
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6 }}>
                {[['all','All'],['marketing','Marketing'],['deals','Deals']].map(([k, label]) => (
                  <button key={k} onClick={() => setTaskKind(k)} style={{
                    padding: '2px 8px', border: 'none', cursor: 'pointer', borderRadius: 4,
                    fontSize: 10.5, fontWeight: taskKind === k ? 600 : 500,
                    background: taskKind === k ? 'var(--surface)' : 'transparent',
                    color: taskKind === k ? 'var(--text)' : 'var(--text-3)',
                    boxShadow: taskKind === k ? 'var(--shadow-sm)' : 'none',
                  }}>{label}</button>
                ))}
              </div>
              <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6 }}>
                {[['all','All'],['manual','Manual'],['automation_rule','Auto']].map(([k, label]) => (
                  <button key={k} onClick={() => setTaskSource(k)} style={{
                    padding: '2px 8px', border: 'none', cursor: 'pointer', borderRadius: 4,
                    fontSize: 10.5, fontWeight: taskSource === k ? 600 : 500,
                    background: taskSource === k ? 'var(--surface)' : 'transparent',
                    color: taskSource === k ? 'var(--text)' : 'var(--text-3)',
                    boxShadow: taskSource === k ? 'var(--shadow-sm)' : 'none',
                  }}>{label}</button>
                ))}
              </div>
              <div style={{ display: 'inline-flex', gap: 0, padding: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6 }}>
                {[['open','Open'],['overdue','Overdue'],['done','Done']].map(([k, label]) => (
                  <button key={k} onClick={() => setTaskFilter(k)} style={{
                    padding: '2px 8px', border: 'none', cursor: 'pointer', borderRadius: 4,
                    fontSize: 10.5, fontWeight: taskFilter === k ? 600 : 500,
                    background: taskFilter === k ? 'var(--surface)' : 'transparent',
                    color: taskFilter === k ? 'var(--text)' : 'var(--text-3)',
                    boxShadow: taskFilter === k ? 'var(--shadow-sm)' : 'none',
                  }}>{label}</button>
                ))}
              </div>
              <div style={{ position: 'relative' }}>
                <Btn size="xs" variant={window.activeFilterCount(taskFilters) ? 'accent' : 'outline'}
                  icon={<Icon.Filter size={11} />}
                  onClick={() => setTaskFilterOpen((v) => !v)}>
                  Filter
                  {window.activeFilterCount(taskFilters) > 0 && (
                    <span style={{ marginLeft: 5, fontSize: 10, opacity: 0.85 }}>
                      {window.activeFilterCount(taskFilters)}
                    </span>
                  )}
                </Btn>
                {taskFilterOpen && (
                  <window.FilterPopover
                    filters={taskFilters}
                    setFilters={setTaskFilters}
                    onClose={() => setTaskFilterOpen(false)}
                    spec={[
                      { key: 'priorities', label: 'Priority', type: 'chips',
                        options: [
                          { value: 'urgent', label: 'Urgent' },
                          { value: 'high',   label: 'High' },
                          { value: 'normal', label: 'Normal' },
                          { value: 'low',    label: 'Low' },
                        ] },
                      // Members are the list the task drawer's assignee
                      // picker already loaded — no extra fetch.
                      { key: 'assignee', label: 'Assignee', type: 'select',
                        placeholder: 'Anyone',
                        options: memberOptions },
                      { key: 'coAssignee', label: 'Co-assignee', type: 'select',
                        placeholder: 'Anyone',
                        options: memberOptions,
                        hint: 'Matches tasks where this person is a co-assignee, not the owner.' },
                    ]}
                  />
                )}
              </div>
              <Btn size="xs" variant="accent" icon={<Icon.Plus size={11} />}
                onClick={() => setCreatingTask(true)}>New task</Btn>
            </div>
          )}
        >
          <div>
            <TaskComposer activeUser={activeUser} onCreated={reloadTasks} />
            {tasks === null && <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
            {tasks && tasks.length === 0 && (
              <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>
                {taskFilter === 'overdue' ? 'No overdue tasks. ✅' :
                 taskFilter === 'done'    ? 'No completed tasks yet.' :
                 tab === 'all'            ? 'No open tasks across your team.' :
                                            'No open tasks. Type one above — e.g. "remind me every Monday to check on Acme".'}
              </div>
            )}
            {taskGroups.map(([label, list]) => {
              const collapsible = label === 'Later';
              const collapsed = collapsible && !showLater;
              return (
              <React.Fragment key={label || 'all'}>
                {label && (
                  <div
                    onClick={collapsible ? toggleLater : undefined}
                    role={collapsible ? 'button' : undefined}
                    title={collapsible ? (collapsed ? 'Show tasks due later' : 'Hide tasks due later') : undefined}
                    style={{
                      padding: '5px 14px', fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4,
                      textTransform: 'uppercase', background: 'var(--surface-2)',
                      borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
                      color: label === 'Overdue' ? 'var(--rose-fg)' : 'var(--text-3)',
                      display: 'flex', alignItems: 'center', gap: 6,
                      cursor: collapsible ? 'pointer' : 'default', userSelect: 'none',
                    }}>
                    {collapsible && (
                      <Icon.ChevronR size={10} color="var(--text-4)"
                        style={{ transform: collapsed ? 'none' : 'rotate(90deg)', transition: 'transform .12s ease' }} />
                    )}
                    {label}<span className="num" style={{ fontWeight: 500 }}>{list.length}</span>
                    {label === 'Overdue' && (
                      <button onClick={(e) => { e.stopPropagation(); snoozeAllOverdue(); }} title="Reschedule every overdue task to today" style={{
                        marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer',
                        padding: 0, fontSize: 10.5, fontWeight: 600, letterSpacing: 0, textTransform: 'none',
                        color: 'var(--text-3)', textDecoration: 'underline',
                      }}>move all to today</button>
                    )}
                    {collapsible && (
                      <span style={{ marginLeft: 'auto', fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'var(--text-4)' }}>
                        {collapsed ? 'Show' : 'Hide'}
                      </span>
                    )}
                  </div>
                )}
                {!collapsed && list.map((t, i) => {
                  const ps = taskToggle.stateFor(t.id);
                  const checked = t.status === 'completed' || !!ps;
                  const lingering = ps && ps.phase === 'done';
                  const overdue = !checked && new Date(`${t.due_date}T23:59:59`) < new Date();
                  const pHue =
                    t.priority === 'urgent' ? 'rose' :
                    t.priority === 'high'   ? 'amber' :
                    t.priority === 'low'    ? 'slate' : 'blue';
                  const expanded = !!expandedTasks[t.id];
                  const noteText = (t.notes || '').trim();
                  const notesOpen = !!expandedNotes[t.id];
                  return (
                    <React.Fragment key={t.id}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                      borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                      opacity: t.status === 'completed' ? 0.5 : 1,
                      background: lingering ? 'var(--green-bg)' : 'transparent',
                      transition: 'background .3s ease',
                    }}>
                      <button onClick={() => taskToggle.toggle(t)} title={t.recurrence ? 'Complete — the next occurrence will be scheduled' : 'Toggle complete'} style={{
                        width: 16, height: 16, borderRadius: 4,
                        border: checked ? 'none' : '1.5px solid var(--border-strong)',
                        background: checked ? 'oklch(0.55 0.12 155)' : 'var(--surface)',
                        cursor: 'pointer', padding: 0, flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 11,
                        animation: ps ? 'atlas-check-pop .25s ease' : 'none',
                      }}>{checked ? '✓' : ''}</button>
                      <span className="num" title={overdue ? `Overdue — was due ${t.due_date}` : `Due ${t.due_date}`} style={{
                        width: 56, flexShrink: 0, fontSize: 11, lineHeight: 1.2, whiteSpace: 'nowrap',
                        fontWeight: overdue ? 600 : 500,
                        color: overdue ? 'var(--rose-fg)' : 'var(--text-3)',
                      }}>{fmtDueShort(t.due_date)}</span>
                      <button onClick={() => {
                        // where the task's work happens (a CIM section's task opens that section)
                        if (t.link) window.location.hash = '#' + t.link;
                        else if (t.engagement_id) setDealId(t.engagement_id);
                        else if (t.company_id) window.AtlasNav?.goToCompany?.(t.company_id);
                        else if (t.contact_id) window.AtlasNav?.goToContact?.(t.contact_id);
                      }} style={{
                        flex: 1, minWidth: 0, textAlign: 'left',
                        background: 'transparent', border: 'none',
                        cursor: (t.engagement_id || t.company_id || t.contact_id) ? 'pointer' : 'default',
                        padding: 0,
                      }}>
                        <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)', textDecoration: checked ? 'line-through' : 'none', overflowWrap: 'anywhere' }}>
                          {t.title}
                          {(t.recurrence_text || t.recurrence) && (
                            <span title="Recurring — completing it schedules the next occurrence" style={{
                              marginLeft: 6, fontSize: 10.5, fontWeight: 500, color: 'var(--text-3)',
                              background: 'var(--surface-2)', border: '1px solid var(--border)',
                              borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap',
                            }}>↻ {t.recurrence_text || 'repeats'}</span>
                          )}
                          {tab === 'all' && t.assigned_to_name && t.assigned_to !== activeUser?.id && (
                            <span title={`Assigned to ${t.assigned_to_name}`} style={{
                              marginLeft: 6, fontSize: 10.5, fontWeight: 500, color: 'var(--text-2)',
                              background: 'var(--surface-2)', border: '1px solid var(--border)',
                              borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap',
                            }}>{t.assigned_to_name}</span>
                          )}
                          {(t.co_assignee_names || []).length > 0 && (
                            <span title={`Co-assigned: ${t.co_assignee_names.join(', ')}`} style={{
                              marginLeft: 6, fontSize: 10.5, fontWeight: 500, color: 'var(--text-3)',
                              background: 'var(--surface-2)', border: '1px solid var(--border)',
                              borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap',
                            }}>👥 {t.co_assignee_names.length === 1 ? t.co_assignee_names[0] : `+${t.co_assignee_names.length}`}</span>
                          )}
                          {t.source === 'automation_rule' && (
                            <span title={`Created automatically${t.rule_key ? ` — rule: ${t.rule_key}` : ''}`} style={{
                              marginLeft: 6, fontSize: 10.5, fontWeight: 500, color: 'var(--text-3)',
                              background: 'var(--surface-2)', border: '1px dashed var(--border-strong)',
                              borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap',
                            }}>⚙ auto</span>
                          )}
                          {t.subtask_total > 0 && (
                            <span
                              onClick={(e) => { e.stopPropagation(); toggleExpand(t.id); }}
                              title={expanded ? 'Hide subtasks' : 'Show subtasks'}
                              style={{
                                marginLeft: 6, fontSize: 10.5, fontWeight: 600, cursor: 'pointer',
                                color: t.subtask_done === t.subtask_total ? 'var(--green-fg)' : 'var(--text-3)',
                                background: 'var(--surface-2)', border: '1px solid var(--border)',
                                borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap',
                              }}>{expanded ? '▾' : '▸'} {t.subtask_done}/{t.subtask_total}</span>
                          )}
                          {noteText && (
                            <span
                              onClick={(e) => { e.stopPropagation(); toggleNotes(t.id); }}
                              title={notesOpen ? 'Hide note' : 'Show note'}
                              style={{
                                marginLeft: 6, fontSize: 10.5, fontWeight: 500, cursor: 'pointer',
                                color: 'var(--text-3)',
                                background: 'var(--surface-2)', border: '1px solid var(--border)',
                                borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap',
                              }}>{notesOpen ? '▾' : '▸'} note</span>
                          )}
                        </div>
                        {(t.contact_name || t.engagement_name || t.company_name) && (
                          <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            {t.contact_name && (
                              <span
                                onClick={(e) => { e.stopPropagation(); window.AtlasNav?.goToContact?.(t.contact_id); }}
                                title="Open contact"
                                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                              >👤 {t.contact_name}</span>
                            )}
                            {t.contact_name && (t.engagement_name || t.company_name) && <span>·</span>}
                            {(t.engagement_name || t.company_name) && <span>{t.engagement_name || t.company_name}</span>}
                          </div>
                        )}
                      </button>
                      {lingering ? (
                        <>
                          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--green-fg)', flexShrink: 0 }}>
                            {ps.spawnedDue ? `Done — next ${window.fmtTaskDue(ps.spawnedDue)}` : 'Done'}
                          </span>
                          {!t.recurrence && !followedUp[t.id] && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                              <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>follow up</span>
                              {[['+3d', 3], ['+1w', 7], ['+1mo', 30]].map(([label, days]) => (
                                <button key={label} onClick={() => createFollowUp(t, days)} style={{
                                  border: '1px solid var(--border-strong)', borderRadius: 5,
                                  background: 'var(--surface)', cursor: 'pointer', padding: '1px 7px',
                                  fontSize: 10.5, fontWeight: 600, color: 'var(--text-2)',
                                }}>{label}</button>
                              ))}
                            </span>
                          )}
                          {followedUp[t.id] && (
                            <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--green-fg)', flexShrink: 0 }}>
                              follow-up {window.fmtTaskDue(followedUp[t.id])} ✓
                            </span>
                          )}
                          <button onClick={() => taskToggle.undo(t)} style={{
                            border: '1px solid var(--border-strong)', borderRadius: 5, flexShrink: 0,
                            background: 'var(--surface)', cursor: 'pointer', padding: '2px 8px',
                            fontSize: 11, fontWeight: 600, color: 'var(--text-2)',
                          }}>Undo</button>
                        </>
                      ) : (
                        <>
                          {snoozingId === t.id ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                              <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>snooze to</span>
                              {[['Tomorrow', 1], ['+1w', 7], ['+1mo', 30]].map(([label, days]) => (
                                <button key={label} onClick={() => snoozeTask(t, days)} style={{
                                  border: '1px solid var(--border-strong)', borderRadius: 5,
                                  background: 'var(--surface)', cursor: 'pointer', padding: '1px 7px',
                                  fontSize: 10.5, fontWeight: 600, color: 'var(--text-2)',
                                }}>{label}</button>
                              ))}
                              <button onClick={() => setSnoozingId(null)} title="Cancel" style={{
                                border: 'none', background: 'transparent', cursor: 'pointer',
                                padding: '1px 4px', fontSize: 11, color: 'var(--text-3)',
                              }}>✕</button>
                            </span>
                          ) : (
                            <>
                              <Pill hue={pHue} sz="sm">{t.priority}</Pill>
                            </>
                          )}
                          {t.status !== 'completed' && snoozingId !== t.id && (
                            <button onClick={() => setSnoozingId(t.id)} title="Snooze — push the due date out" style={{
                              width: 22, height: 22, borderRadius: 5, border: 'none', flexShrink: 0,
                              background: 'transparent', cursor: 'pointer', padding: 0,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              color: 'var(--text-3)',
                            }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            ><Icon.Clock size={12} /></button>
                          )}
                          <button onClick={() => setExpandedTasks((m) => ({ ...m, [t.id]: true }))} title="Add subtask" style={{
                            width: 22, height: 22, borderRadius: 5, border: 'none', flexShrink: 0,
                            background: 'transparent', cursor: 'pointer', padding: 0,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--text-3)',
                          }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          ><Icon.Plus size={12} /></button>
                          <button onClick={() => setEditingTask(t)} title="Edit task" style={{
                            width: 22, height: 22, borderRadius: 5, border: 'none', flexShrink: 0,
                            background: 'transparent', cursor: 'pointer', padding: 0,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--text-3)',
                          }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          ><Icon.Edit size={12} /></button>
                        </>
                      )}
                    </div>
                    {noteText && notesOpen && (
                      <div style={{
                        padding: '0 14px 10px 40px', fontSize: 11.5, lineHeight: 1.5,
                        color: 'var(--text-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>{noteText}</div>
                    )}
                    {expanded && (
                      <div style={{ padding: '2px 14px 10px 40px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {(t.subtasks || []).map((sub) => {
                          const sps = taskToggle.stateFor(sub.id);
                          const sChecked = sub.status === 'completed' || !!sps;
                          const sLinger = sps && sps.phase === 'done';
                          return (
                            <div key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <button onClick={() => taskToggle.toggle(sub)} title="Toggle complete" style={{
                                width: 14, height: 14, borderRadius: 4,
                                border: sChecked ? 'none' : '1.5px solid var(--border-strong)',
                                background: sChecked ? 'oklch(0.55 0.12 155)' : 'var(--surface)',
                                cursor: 'pointer', padding: 0, flexShrink: 0,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontSize: 10,
                                animation: sps ? 'atlas-check-pop .25s ease' : 'none',
                              }}>{sChecked ? '✓' : ''}</button>
                              <span style={{
                                fontSize: 12, color: 'var(--text-2)', flex: 1, minWidth: 0,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                textDecoration: sChecked ? 'line-through' : 'none',
                                opacity: sub.status === 'completed' ? 0.55 : 1,
                              }}>{sub.title}</span>
                              {sLinger ? (
                                <button onClick={() => taskToggle.undo(sub)} style={{
                                  border: '1px solid var(--border-strong)', borderRadius: 5, flexShrink: 0,
                                  background: 'var(--surface)', cursor: 'pointer', padding: '1px 7px',
                                  fontSize: 10.5, fontWeight: 600, color: 'var(--text-2)',
                                }}>Undo</button>
                              ) : (
                                <span className="num" style={{ fontSize: 10.5, color: 'var(--text-4)', flexShrink: 0 }}>{sub.due_date}</span>
                              )}
                            </div>
                          );
                        })}
                        <input
                          value={subDrafts[t.id] || ''}
                          onChange={(e) => setSubDrafts((d) => ({ ...d, [t.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') addSubtask(t); }}
                          placeholder="Add a subtask — Enter to save"
                          style={{
                            maxWidth: 320, fontSize: 12, padding: '4px 8px', marginTop: 2,
                            border: '1px solid var(--border)', borderRadius: 6,
                            background: 'var(--surface)', color: 'var(--text)',
                          }}
                        />
                      </div>
                    )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
              );
            })}
          </div>
        </Section>

        {inboxPending.length > 0 && (
          <Section title={`Atlas Inbox · ${inboxPending.length} awaiting your call`}>
            <div>
              {inboxPending.map((s, i) => (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                }}>
                  <span title={`Forwarded by ${s.forwarder || ''}`} style={{ fontSize: 13, flexShrink: 0 }}>📨</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{s.subject || '(no subject)'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.classification}
                      {s.person && (s.person.name || s.person.email) ? ` · ${s.person.name || s.person.email}` : ''}
                      {s.summary ? ` — ${s.summary}` : ''}
                    </div>
                    {s.request ? (
                      <div style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        You asked: {s.request}
                      </div>
                    ) : null}
                  </div>
                  {(s.options || []).slice(0, 3).map((o, j) => (
                    <Btn key={j} size="xs" variant={o.style === 'primary' ? 'accent' : undefined}
                      onClick={() => actInbox(s, o)}>
                      {o.verb === 'ignore' ? 'Ignore' : o.label}
                    </Btn>
                  ))}
                  {(s.options || []).length > 3 && !(s.options || []).slice(0, 3).some((o) => o.verb === 'ignore') && (
                    <Btn size="xs" onClick={() => actInbox(s, { verb: 'ignore' })}>Ignore</Btn>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}
        <Section
          title={`Automations${(proposals || []).length ? ` · ${proposals.length} suggested` : ''}`}
        >
          <div>
            {(proposals || []).map((p, i) => (
              <div key={p.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
              }}>
                <span title={`Suggested by: ${p.rule_key}`} style={{ fontSize: 13, flexShrink: 0 }}>✨</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{p.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.notes}{p.company_name ? ` · ${p.company_name}` : ''}
                  </div>
                </div>
                <Btn size="xs" variant="accent" onClick={() => actProposal(p, 'accept')}>Add task</Btn>
                <Btn size="xs" onClick={() => actProposal(p, 'dismiss')}>Dismiss</Btn>
              </div>
            ))}
            {(proposals || []).length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-3)' }}>
                No pending suggestions.
              </div>
            )}
            <div style={{ borderTop: '1px solid var(--border)', padding: '8px 14px', fontSize: 11.5, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1 }}>
                {taskRules.filter((r) => r.disabled).length
                  ? `${taskRules.length - taskRules.filter((r) => r.disabled).length} of ${taskRules.length} automatic task rules on for you.`
                  : 'All automatic task rules are on for you.'}
              </span>
              <Btn size="xs" onClick={() => setRulesOpen(true)}>Choose…</Btn>
            </div>
          </div>
        </Section>

        <Section
          title={`Engagement alerts · ${fmtAlertCount(filtered.length)}`}
          action={filtered.length > 0 && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-3)', cursor: 'pointer' }}>
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllAlerts}
                style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
              Select all
            </label>
          )}
        >
          <div>
            {selectedAlerts.size > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                borderBottom: '1px solid var(--border)', background: 'var(--accent-soft)',
                position: 'sticky', top: 0, zIndex: 1, flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--accent-text)' }}>
                  {selectedAlerts.size} selected
                </span>
                <div style={{ flex: 1 }} />
                <Select
                  value=""
                  onChange={(e) => bulkReassign(e.target.value)}
                  disabled={bulkBusy}
                  options={[
                    { value: '', label: bulkBusy ? 'Working…' : 'Reassign to…' },
                    ...members.filter((m) => m.active !== false)
                      .map((m) => ({ value: m.id, label: m.full_name })),
                  ]}
                  style={{ width: 170 }}
                />
                <Btn size="xs" variant="outline" onClick={bulkArchive} disabled={bulkBusy}>Archive</Btn>
                <Btn size="xs" variant="ghost" onClick={() => setSelectedAlerts(new Set())} disabled={bulkBusy}>Clear</Btn>
              </div>
            )}
            {filtered.length === 0 && (
              <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)' }}>
                {tab === 'mine'
                  ? 'Nothing assigned to you is exited, quiet 90d+, or on hold without an update in 6mo+. Switch to Team to triage firm-wide alerts.'
                  : 'Nothing exited, quiet 90d+, or on hold without an update in 6mo+. All clear.'}
              </div>
            )}
            {filtered.map(({ d, age, lastTouch }, i) => {
              let reason, hue, icon;
              if (d.stale)      { reason = `Exited${d.exitName ? ' · ' + d.exitName : ''}`;        hue = 'rose';  icon = '✕'; }
              else if (d.onHold){ reason = `On hold · ${d.onHold} · quiet ${Math.round(age)}d`;    hue = 'amber'; icon = '⏸'; }
              else              { reason = `No activity in ${Math.round(age)}d`;                   hue = 'slate'; icon = '⌛'; }
              const checked = selectedAlerts.has(d.id);
              return (
                <div key={d.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  background: checked ? 'var(--accent-soft)' : 'transparent',
                }}
                  onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = 'var(--surface-2)'; }}
                  onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleAlert(d.id)}
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }} />
                  <button onClick={() => { setDealId(d.id); }} style={{
                    display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0,
                    background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0,
                  }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 24, height: 24, borderRadius: 6, fontSize: 11,
                      background: HUE[hue].bg, color: HUE[hue].fg, flexShrink: 0,
                    }}>{icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                        {d.stageName || d.stage} · <UserLink id={d._raw?.dealmaker_id} name={d.owner} style={{ color: 'var(--text-2)' }} /> · last activity {lastTouch != null ? relativeTime(new Date(lastTouch).toISOString()) : '—'}
                      </div>
                    </div>
                    <Pill hue={hue} dot sz="sm">{reason}</Pill>
                    <Icon.ChevronR size={12} color="var(--text-4)" />
                  </button>
                </div>
              );
            })}
          </div>
        </Section>

        </div>

        {/* Right: agent activity */}
        <Section
          title="Recent agent activity"
          action={<Btn size="xs" variant="ghost" onClick={() => setView('agents')}>Open agents</Btn>}
        >
          <div>
            {workspaces === null && (
              <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
            )}
            {workspaces && recentWorkspaces.length === 0 && (
              <div style={{ padding: '16px 14px', fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
                No workspace activity yet.
              </div>
            )}
            {recentWorkspaces.map((w, i) => {
              const status = w.latest_run_status || (w.active_run_count > 0 ? 'running' : 'idle');
              const sHue = status === 'running' ? 'blue'
                         : status === 'completed' ? 'green'
                         : status === 'failed' ? 'rose'
                         : 'slate';
              return (
                <div key={w.id} style={{ display: 'flex', gap: 10, padding: '10px 14px', alignItems: 'flex-start', borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 5, background: HUE.blue.bg, color: HUE.blue.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon.Bot size={11} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{w.name}</span>
                      <Pill hue={sHue} dot sz="sm">{status}</Pill>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                      {w.lead_agent_key.replace(/-/g, ' ')} · {w.run_count} runs
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}>
                    {relativeTime(w.updated_at)}
                  </span>
                </div>
              );
            })}
          </div>
        </Section>
      </div>

      {creatingTask && (
        <TaskDrawer
          activeUser={activeUser}
          members={members}
          onClose={() => setCreatingTask(false)}
          onCreated={reloadTasks}
        />
      )}
      {editingTask && (
        <TaskDrawer
          activeUser={activeUser}
          members={members}
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onCreated={reloadTasks}
        />
      )}
    </div>
  );
};

// Task pieces the phone pages (src/mobile/*.jsx) reuse.
Object.assign(window, { TaskDrawer, TaskComposer, NotifBell, signOutOfAtlas, TaskForm, taskFormDraft, taskFormValid, submitTaskForm });

// Gate the SPA on Supabase Auth. If no session, the user sees a login
// screen; once they click their magic link, the hash gets consumed on
// mount and they land in <App />.
const AuthGate = window.AtlasAuth?.AuthGate;
// OAuth consent hand-off (Atlas MCP Phase 3): Supabase's OAuth server
// redirects here with ?authorization_id=… for the signed-in user to approve
// an MCP client. Render the consent screen instead of the app shell — still
// inside AuthGate, so an anonymous user logs in first and lands back on
// consent. Query params (not hash) so this coexists with the hash router.
//
// Read when Inner first renders, not at load: the Microsoft round-trip drops
// the query, and AuthGate only puts ?authorization_id= back (login.jsx,
// consumeHashSession) after this file has loaded.
const consentAuthorizationId = () => {
  try {
    return new URLSearchParams(window.location.search).get('authorization_id');
  } catch (_) {
    return null;
  }
};
const Inner = () => {
  const [authorizationId] = useState(consentAuthorizationId);
  return authorizationId && window.OAuthConsent ? (
    <window.OAuthConsent authorizationId={authorizationId} />
  ) : (
    <App />
  );
};
const Root = AuthGate ? (
  <AuthGate><Inner /></AuthGate>
) : (
  // login.jsx didn't load (dev or misconfigured) — fall back to bare app
  // so we don't black-screen. The legacy bearer token still works.
  <Inner />
);
// Last resort: a throw in the shell itself (sidebar, top bar, AuthGate) still
// shows a Reload instead of an empty #root.
ReactDOM.createRoot(document.getElementById('root')).render(
  <ErrorBoundary name="root">{Root}</ErrorBoundary>
);
